// Ensure you include Stripe.js in your public/index.html:
// <script src="https://js.stripe.com/v3"></script>

import MetaTagManager from './MetaTagManager';
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, Link } from 'react-router-dom';
import StarfieldHero from './components/StarfieldHero';
import NeighboringStars from './components/NeighboringStars';
import CandleVigil from './components/CandleVigil';
import RemembranceDates from './components/RemembranceDates';
import LightStar from './components/LightStar';

// Rotating invitations for the memory wall — a question invites where a
// blank box intimidates.
const MEMORY_PROMPTS = [
  'What is a meal you shared with them that you still remember?',
  'What would you tell them if you had one more minute?',
  'What did they teach you without meaning to?',
  'What made them laugh until they cried?',
  'Where do you feel closest to them now?',
  'What song, smell, or place brings them back to you?',
];
import { db, auth } from './firebase';
import { activateForeverMemorial } from './galaxy/claims';
import LettersToHeaven from './LettersToHeaven';
import FinalRestingPlace from './FinalRestingPlace';
import FamilyTreeSection from './FamilyTreeSection';
import ServiceInvitation from './ServiceInvitation';
import DigitalFlowersSection from './DigitalFlowersSection';
import DigitalCardsSection from './DigitalCardsSection';
import FundraisingSection from './FundraisingSection';
import ServiceProgramSection from './ServiceProgramSection';


// FUTURE DEVELOPMENT: Family Tree Features
// import FamilyTree from './FamilyTree';
// import FamilyManager from './FamilyManager';
// NEW: Import timeline component
import MemorialTimeline from './MemorialTimeline';

// STEP 1.3: Import helper functions
import { getUserSubscription, isSubscriptionActive } from './SubscriptionUtils';
import { events } from './analytics';
import {
  doc,
  addDoc,
  collection,
  query,
  onSnapshot,
  orderBy,
  setDoc,
  deleteDoc,
  increment,
  updateDoc,
  where,
  getDocs,
  serverTimestamp,
} from 'firebase/firestore';

// Lowercase ordinal suffix helper
const getSuffix = (d) => {
  if (d > 3 && d < 21) return 'th';
  switch (d % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default:  return 'th';
  }
};

// Dates are stored as plain "YYYY-M-D" strings, sometimes without zero
// padding. `new Date()` on those is a trap twice over: Safari refuses the
// unpadded form outright (rendering "Invalid Date NaNth, NaN"), and even the
// padded form parses as UTC midnight, which shows the previous day west of
// Greenwich. Read the parts directly — a calendar date has no timezone.
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const parseDateParts = (value) => {
  if (!value) return null;
  if (typeof value === 'string') {
    const parts = value.trim().split('-').map((n) => parseInt(n, 10));
    if (parts.length === 3 && !parts.some(Number.isNaN) && parts[1] >= 1 && parts[1] <= 12) {
      return { y: parts[0], m: parts[1], d: parts[2] };
    }
  }
  const dt = value && value.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(dt.getTime())) return null;
  return { y: dt.getFullYear(), m: dt.getMonth() + 1, d: dt.getDate() };
};

const formatDate = (dateStr) => {
  const p = parseDateParts(dateStr);
  if (!p) return '';
  const day = p.d;
  const month = SHORT_MONTHS[p.m - 1];
  const year = p.y;
  return (
    <>
      {month} {day}
      <sup style={{ fontVariant: 'small-caps' }}>{getSuffix(day)}</sup>, {year}
    </>
  );
};

// ── Forever (pre-need): the living-star banner ──────────────────────────
// Shown on memorials whose soul is still among us. Everyone sees the
// silver-blue "still shining" state; only the owner or a designated
// steward sees the quiet doorway to activation, for when the time comes.
const LivingStarBanner = ({ memorial, memorialId, canActivate }) => {
  const [activating, setActivating] = useState(false);
  const [dateOfPassing, setDateOfPassing] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const activate = async () => {
    if (!dateOfPassing) { setError('Please choose the date of passing.'); return; }
    setBusy(true);
    setError('');
    try {
      await activateForeverMemorial(db, {
        memorialId,
        starId: memorial.galaxyLocation?.starId || null,
        user: auth.currentUser,
        deathDate: new Date(dateOfPassing),
      });
      window.location.reload();
    } catch (e) {
      setError(e.message || 'Activation failed. Please try again.');
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-2xl p-6 md:p-8 mb-10 text-center"
      style={{
        background: 'radial-gradient(ellipse at 50% 0%, #141b2e 0%, #0a0d1a 85%)',
        border: '1px solid rgba(184,205,240,0.35)',
        boxShadow: '0 0 40px rgba(184,205,240,0.08)',
      }}
    >
      <div
        className="mx-auto mb-4 w-2.5 h-2.5 rounded-full"
        style={{ background: '#b8cdf0', boxShadow: '0 0 18px #b8cdf0, 0 0 44px rgba(184,205,240,0.5)', animation: 'soulBreathe 4.5s ease-in-out infinite' }}
      />
      <p className="text-[10.5px] uppercase" style={{ letterSpacing: '0.34em', color: '#8fa9d6' }}>
        A Living Star
      </p>
      <h3 className="mt-2 text-xl md:text-2xl font-extralight text-white" style={{ letterSpacing: '0.05em' }}>
        Still shining among us
      </h3>
      <p className="mt-3 text-sm font-light leading-relaxed max-w-lg mx-auto" style={{ color: 'rgba(214,226,245,0.75)' }}>
        {capitalizeWords(memorial.firstName)} chose their star while living — writing
        their story in their own words, in their own time. When the time comes,
        this page will become their memorial, and their star will turn gold.
      </p>

      {canActivate && !activating && (
        <button
          onClick={() => setActivating(true)}
          className="mt-6 px-7 py-3 rounded-full text-[11px] uppercase transition-all"
          style={{
            letterSpacing: '0.22em',
            color: '#ffe2a8',
            border: '1px solid rgba(184,150,79,0.45)',
            background: 'rgba(255,255,255,0.05)',
          }}
        >
          The time has come
        </button>
      )}

      {canActivate && activating && (
        <div className="mt-6 max-w-xs mx-auto text-left">
          <p className="text-xs font-light mb-4 text-center" style={{ color: 'rgba(214,226,245,0.7)' }}>
            We're so sorry. Setting the date of passing will turn their star
            gold and open this memorial to remembrance.
          </p>
          <label className="block text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: '#8fa9d6' }}>
            Date of passing
          </label>
          <input
            type="date"
            value={dateOfPassing}
            onChange={(e) => setDateOfPassing(e.target.value)}
            className="w-full pb-2 border-0 border-b bg-transparent text-white focus:outline-none transition-all font-light"
            style={{ borderColor: 'rgba(184,205,240,0.4)', colorScheme: 'dark' }}
          />
          {error && <p className="mt-3 text-xs text-red-300 text-center">{error}</p>}
          <div className="mt-5 flex items-center gap-3">
            <button
              onClick={() => { setActivating(false); setError(''); }}
              className="px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-white/50 hover:text-white transition-colors"
            >
              Not yet
            </button>
            <button
              onClick={activate}
              disabled={busy}
              className="flex-1 rounded-full py-3 text-[11px] uppercase tracking-[0.2em] transition-all disabled:opacity-50"
              style={{ background: '#e5c883', color: '#0d0b16' }}
            >
              {busy ? 'Turning their star gold…' : '✦ Activate the memorial'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to extract YouTube video ID
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  
  return (match && match[2].length === 11) ? match[2] : null;
};

// Helper function to capitalize words
const capitalizeWords = (words) => {
  if (!words) return '';
  return words
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};


const formatEmailToName = (email) => {
  if (!email) return null;
  const emailPrefix = email.split('@')[0];
  return emailPrefix
    .split(/[._-]/)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

// Facebook-style photo gallery — collage grid on the page, immersive
// full-screen stacked viewer when opened. Free plan shows the first
// FREE_GALLERY_LIMIT photos; an active creator subscription shows all.
const FREE_GALLERY_LIMIT = 5;

const SimpleGallery = ({ images = [], memorialName = "Memorial", canShowAll = true, isOwner = false, checking = false }) => {
  const [viewerOpen, setViewerOpen] = useState(false);
  const [startIndex, setStartIndex] = useState(0);
  const photoRefs = useRef([]);

  const visibleImages = canShowAll ? images : images.slice(0, FREE_GALLERY_LIMIT);
  const hiddenCount = images.length - visibleImages.length;

  // Collage shows at most 4 tiles: one large photo, then a row of three.
  const tiles = visibleImages.slice(0, 4);
  const beyondCollage = visibleImages.length - tiles.length;

  const openViewer = (index) => {
    events.galleryOpened(images.length);
    setStartIndex(index);
    setViewerOpen(true);
  };

  // While the viewer is open: lock page scroll, close on Escape, and
  // scroll the tapped photo into view.
  useEffect(() => {
    if (!viewerOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') setViewerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    requestAnimationFrame(() => {
      const el = photoRefs.current[startIndex];
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'start' });
    });
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [viewerOpen, startIndex]);

  const handleImgError = (e) => {
    e.target.src = '/assets/placeholder-image.jpg';
    e.target.onerror = null;
  };

  const tileImg = (url, idx) => (
    <img
      src={url}
      alt={`${memorialName} — photo ${idx + 1}`}
      className="w-full h-full object-cover hover:opacity-95 transition-opacity duration-200"
      loading="lazy"
      onError={handleImgError}
    />
  );

  return (
    <div className="bg-white p-6 md:p-10 mb-8 border border-[#eee9df]">
      <div className="text-center mb-8">
        <p className="text-[10.5px] uppercase" style={{ letterSpacing: '0.34em', color: '#b8964f' }}>Their Moments</p>
        <h2 className="mt-3 text-2xl md:text-3xl font-extralight text-gray-900" style={{ letterSpacing: '0.04em' }}>Gallery</h2>
        <div className="mx-auto mt-4" style={{ width: '54px', height: '1px', background: 'linear-gradient(90deg, transparent, #cbab6a, transparent)' }} />
      </div>

      {/* Facebook-style collage */}
      {visibleImages.length === 1 && (
        <div
          onClick={() => openViewer(0)}
          className="cursor-pointer overflow-hidden bg-gray-100"
          style={{ maxHeight: '540px' }}
        >
          <img
            src={visibleImages[0]}
            alt={`${memorialName} — photo 1`}
            className="w-full object-cover hover:opacity-95 transition-opacity duration-200"
            style={{ maxHeight: '540px' }}
            loading="lazy"
            onError={handleImgError}
          />
        </div>
      )}

      {visibleImages.length === 2 && (
        <div className="grid grid-cols-2 gap-1">
          {tiles.map((url, idx) => (
            <div key={idx} onClick={() => openViewer(idx)} className="relative aspect-square overflow-hidden cursor-pointer bg-gray-100">
              {tileImg(url, idx)}
            </div>
          ))}
        </div>
      )}

      {visibleImages.length >= 3 && (
        <div className="space-y-1">
          {/* Hero photo */}
          <div
            onClick={() => openViewer(0)}
            className="relative overflow-hidden cursor-pointer bg-gray-100"
            style={{ aspectRatio: '16 / 10' }}
          >
            {tileImg(visibleImages[0], 0)}
          </div>
          {/* Row of thumbnails */}
          <div className={`grid ${tiles.length - 1 === 2 ? 'grid-cols-2' : 'grid-cols-3'} gap-1`}>
            {tiles.slice(1).map((url, i) => {
              const idx = i + 1;
              const isLastTile = idx === tiles.length - 1;
              return (
                <div key={idx} onClick={() => openViewer(idx)} className="relative aspect-square overflow-hidden cursor-pointer bg-gray-100">
                  {tileImg(url, idx)}
                  {isLastTile && beyondCollage > 0 && (
                    <div className="absolute inset-0 bg-black/55 hover:bg-black/65 transition-colors flex items-center justify-center">
                      <span className="text-white text-2xl md:text-3xl font-light">+{beyondCollage}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* View-all button */}
      {visibleImages.length > 1 && (
        <div className="text-center mt-6">
          <button onClick={() => openViewer(0)} className="sm-btn-primary">
            View All {visibleImages.length} Photos
          </button>
        </div>
      )}

      {/* Free-plan note — shown to the memorial owner only */}
      {hiddenCount > 0 && isOwner && !checking && (
        <div className="mt-6 rounded-2xl border border-dashed border-[#d8c9a4] bg-[#fdfbf6] p-5 text-center">
          <p className="text-sm text-gray-600 font-light">
            Showing {FREE_GALLERY_LIMIT} of {images.length} photos — the rest are kept safe for you.
          </p>
          <Link
            to="/subscription"
            className="inline-block mt-3 px-5 py-2.5 rounded-full text-xs bg-white border border-[#eae6dd] hover:border-[#d8c9a4] hover:shadow-[0_4px_18px_rgba(184,150,79,0.12)] transition-all"
            style={{ color: '#96793c', textDecoration: 'none' }}
          >
            ✦ Unlock the full gallery with a subscription
          </Link>
        </div>
      )}

      {/* Facebook-style full-screen viewer — stacked photos on black.
          Rendered into document.body via portal so no page container,
          transform, or z-index stack can trap or cover it. */}
      {viewerOpen && createPortal(
        <div className="fixed inset-0 bg-black flex flex-col" style={{ zIndex: 100000 }}>
          {/* Header — padded below the iPhone notch / status bar */}
          <div
            className="flex items-center justify-between px-4 pb-3 border-b border-white/10"
            style={{ background: 'rgba(0,0,0,0.9)', paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
          >
            <div className="min-w-0">
              <p className="text-white text-sm font-light truncate">{memorialName}</p>
              <p className="text-white/50 text-[11px] tracking-[0.14em] uppercase">
                {visibleImages.length} photo{visibleImages.length === 1 ? '' : 's'}
              </p>
            </div>
            <button
              onClick={() => setViewerOpen(false)}
              aria-label="Close photo viewer"
              className="ml-4 shrink-0 text-white/85 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-all"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Stacked photos — tapping the black background also closes */}
          <div className="flex-1 overflow-y-auto overscroll-contain" onClick={() => setViewerOpen(false)}>
            <div
              className="max-w-3xl mx-auto pb-12"
              style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 48px)' }}
              onClick={(e) => e.stopPropagation()}
            >
              {visibleImages.map((url, idx) => (
                <div key={idx} ref={(el) => { photoRefs.current[idx] = el; }} className="mb-1">
                  <img
                    src={url}
                    alt={`${memorialName} — photo ${idx + 1}`}
                    className="w-full h-auto"
                    loading="lazy"
                    onError={handleImgError}
                  />
                  <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-white/45 text-[11px] tracking-[0.18em] uppercase">
                      Photo {idx + 1} of {visibleImages.length}
                    </span>
                    <span style={{ color: '#cbab6a' }} className="text-xs">✦</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Updated FloatingCompactSocialShare component - Squarespace style
const FloatingCompactSocialShare = ({ memorial, memorialSlug, visible = true }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const tributeLink = `${window.location.origin}/memorial/${memorialSlug}`;

  const shareText = `Remembering ${capitalizeWords(memorial.prefix)} ${capitalizeWords(memorial.firstName)} ${capitalizeWords(memorial.lastName)} - A beautiful life well lived.`;
  const shareTextPlain = `Remembering ${capitalizeWords(memorial.prefix)} ${capitalizeWords(memorial.firstName)} ${capitalizeWords(memorial.lastName)}`;

  const shareToFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(tributeLink)}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareToTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTextPlain)}&url=${encodeURIComponent(tributeLink)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareViaEmail = () => {
    const subject = `Remembering ${capitalizeWords(memorial.prefix)} ${capitalizeWords(memorial.firstName)} ${capitalizeWords(memorial.lastName)}`;
    const body = `I wanted to share this beautiful memorial with you.\n\n${shareText}\n\n${tributeLink}\n\nCreated with love on SouleMap Memorials.`;
    const url = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
  };

  // Simplified shareLink function - relies on meta tags for images
  const shareLink = async () => {
    
    
    // Restored to the combination that verifiably worked (git 74e2fd3):
    // short plain text with NO link inside it, and the URL in its own field.
    // Embedding the link in the text is what broke Facebook's composer.
    const shareData = {
      title: `${capitalizeWords(memorial.prefix)} ${capitalizeWords(memorial.firstName)} ${capitalizeWords(memorial.lastName)} - Memorial`,
      text: shareTextPlain,
      url: tributeLink
    };
    
    // Check if Web Share API is supported (mobile)
    if (navigator.share) {
      try {
        
        await navigator.share(shareData);
      } catch (err) {
        // User cancelled or error occurred
        if (err.name !== 'AbortError') {
          console.error('Error sharing:', err);
          await fallbackCopyToClipboard();
        }
      }
    } else {
      // Fallback to copying link to clipboard (desktop)
      await fallbackCopyToClipboard();
    }
  };

  // Helper function for clipboard fallback
  const fallbackCopyToClipboard = async () => {
    try {
      const shareText = `${capitalizeWords(memorial.prefix)} ${capitalizeWords(memorial.firstName)} ${capitalizeWords(memorial.lastName)} - Memorial\n\n${shareTextPlain}\n\n${tributeLink}`;
      await navigator.clipboard.writeText(shareText);
      alert('Memorial link copied to clipboard!\n\nWhen you paste this link, social platforms will automatically show the memorial image and details.');
    } catch (err) {
      console.error('Failed to copy link:', err);
      alert(`Failed to copy link. Please copy manually: ${tributeLink}`);
    }
  };

  // Squarespace-style horizontal bar - sharp corners, minimal design
  return (
    <div
      className={`fixed bottom-6 right-6 z-50 transition-all duration-500 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
    >
      <div className="bg-black/90 backdrop-blur-md shadow-2xl transition-all duration-300 rounded-full overflow-hidden" style={{ border: '1px solid rgba(184,150,79,0.4)' }}>
        {!isExpanded ? (
          // Collapsed state - just "Share" button
          <button
            onClick={() => setIsExpanded(true)}
            className="px-4 py-2 md:px-6 md:py-3 text-white hover:text-gray-200 text-xs md:text-sm transition-all duration-200 hover:bg-white/10"
          >
            <span className="text-xs md:text-sm">←</span> Share
          </button>
        ) : (
          // Expanded state - horizontal bar with all options
          <div className="flex items-center">
            <button
              onClick={shareLink}
              className="px-4 py-2 md:px-6 md:py-3 text-white hover:text-gray-200 text-xs md:text-sm border-r border-gray-700 transition-all duration-200 hover:bg-white/10"
            >
              Share Link
            </button>
            
            <button
              onClick={shareViaEmail}
              className="px-4 py-2 md:px-6 md:py-3 text-white hover:text-gray-200 text-xs md:text-sm border-r border-gray-700 transition-all duration-200 hover:bg-white/10"
            >
              E-mail
            </button>
            
            <button
              onClick={shareToTwitter}
              className="px-4 py-2 md:px-6 md:py-3 text-white hover:text-gray-200 text-xs md:text-sm border-r border-gray-700 transition-all duration-200 hover:bg-white/10"
            >
              X/Twitter
            </button>
            
            <button
              onClick={shareToFacebook}
              className="px-4 py-2 md:px-6 md:py-3 text-white hover:text-gray-200 text-xs md:text-sm border-r border-gray-700 transition-all duration-200 hover:bg-white/10"
            >
              Facebook
            </button>
            
            <button
              onClick={() => setIsExpanded(false)}
              className="px-4 py-2 md:px-6 md:py-3 text-white hover:text-gray-200 text-xs md:text-sm transition-all duration-200 hover:bg-white/10"
            >
              <span className="text-xs md:text-sm">→</span> Share
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Memory Card Component - UPDATED with Squarespace styling
function MemoryCard({ 
  memory, 
  index, 
  expandedMemories, 
  setExpandedMemories, 
  showComments, 
  setShowComments, 
  memoryComments, 
  commentTexts, 
  setCommentTexts, 
  submittingComments, 
  handleSubmitComment, 
  getRelativeTime, 
  getUserDisplayName, 
  auth, 
  handleLoginRedirect,
  // Treasure functionality props
  treasureCounts,
  userTreasures,
  handleTreasureMemory,
  treasuringMemories,
  // Delete functionality props
  handleDeleteComment,
  memorialId,
  isMemorialOwner,
  // NEW: Edit functionality props
  editingComments,
  editCommentTexts,
  setEditingComments,
  setEditCommentTexts,
  handleEditComment,
  handleSaveComment,
  handleCancelEdit
}) {
  const shouldTruncate = memory.text && memory.text.length > 150;
  const isExpanded = expandedMemories.has(memory.id);

  // Report flow (App Store UGC rule 1.2): idle → confirm → sent
  const [reportState, setReportState] = useState('idle');
  const submitReport = async () => {
    if (reportState === 'sent') return;
    if (reportState === 'idle') { setReportState('confirm'); return; }
    try {
      await addDoc(collection(db, 'reports'), {
        type: 'memory',
        memorialId,
        memoryId: memory.id,
        excerpt: (memory.text || '').slice(0, 200),
        postedBy: memory.userName || 'Anonymous',
        reportedBy: auth.currentUser ? auth.currentUser.uid : null,
        reportedByEmail: auth.currentUser?.email || null,
        status: 'open',
        timestamp: serverTimestamp(),
      });
      events.contentReported();
      setReportState('sent');
    } catch (e) {
      console.error('report failed', e);
      setReportState('idle');
    }
  };
  const displayText = shouldTruncate && !isExpanded 
    ? memory.text.slice(0, 150) + '...' 
    : memory.text;

  const toggleExpanded = () => {
    const newExpanded = new Set(expandedMemories);
    if (isExpanded) {
      newExpanded.delete(memory.id);
    } else {
      newExpanded.add(memory.id);
    }
    setExpandedMemories(newExpanded);
  };

  const toggleComments = () => {
    const newShowComments = new Set(showComments);
    if (showComments.has(memory.id)) {
      newShowComments.delete(memory.id);
    } else {
      newShowComments.add(memory.id);
    }
    setShowComments(newShowComments);
  };

  // Get display name for memory
  const displayName = memory.userName || 'Anonymous';
  const comments = memoryComments[memory.id] || [];
  const isCommentsVisible = showComments.has(memory.id);

  // Treasure functionality
  const treasureCount = treasureCounts[memory.id] || 0;
  const isUserTreasured = userTreasures.has(memory.id);
  const isTreasuring = treasuringMemories.has(memory.id);
  
  // Determine if this memory should have golden highlighting
  const isHighlyTreasured = treasureCount >= 3; // Threshold for golden highlighting

  // NEW: Check if user can edit/delete a comment
  const canUserModifyComment = (comment) => {
    return auth.currentUser && comment.userId === auth.currentUser.uid;
  };

  return (
    <div className={`group relative w-full max-w-full rounded-2xl p-4 md:p-6 border transition-all duration-300 ${
      isHighlyTreasured
        ? 'bg-[#faf9f7] border-[#e2cf9f] hover:shadow-[0_10px_40px_rgba(184,150,79,0.16)]'
        : 'bg-white border-[#eee9df] hover:border-[#d8c9a4] hover:shadow-[0_10px_40px_rgba(184,150,79,0.12)]'
    }`}>
      {/* Golden treasure indicator for highly treasured memories */}
      {isHighlyTreasured && (
        <div className="absolute -top-2.5 -right-2 rounded-full text-[10px] font-medium uppercase tracking-[0.14em] px-3 py-1 shadow-sm" style={{ background: '#faf6ec', color: '#96793c', border: '1px solid #e2cf9f' }}>
          ✦ Treasured
        </div>
      )}
      
      <div className="flex items-start gap-2 md:gap-4 min-w-0 w-full">
        {/* Avatar */}
        <div className="relative">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center font-light shadow-lg shrink-0 group-hover:shadow-xl transition-shadow duration-300 ${
            'text-[#ffe2a8]'
          }`} style={{
            background: 'radial-gradient(circle at 35% 30%, #2a3152, #0d1020)',
            boxShadow: isHighlyTreasured
              ? '0 0 0 2px #cbab6a, 0 0 14px rgba(255,215,140,0.35)'
              : '0 0 0 1px rgba(255,215,140,0.4)',
          }}>
            {displayName.charAt(0).toUpperCase()}
          </div>
          <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center ${
            'bg-[#1c1930]'
          }`}>
            <span className="text-white text-xs">🕊️</span>
          </div>
        </div>

        <div className="flex-1 min-w-0 w-0">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex flex-col md:flex-row md:items-center md:gap-3">
              <h4 className="font-semibold text-gray-800 text-sm">
                {displayName}
              </h4>
              <div className="hidden md:block w-1 h-1 bg-gray-300"></div>
              <span className="text-xs text-gray-500 font-medium mt-1 md:mt-0">
                {getRelativeTime(memory.timestamp)}
              </span>
            </div>
            {/* Memory number badge */}
            <div className={`text-xs font-medium px-2 py-1 rounded-full ${
              'bg-[#f6f4ef] text-[#b8964f]'
            }`}>
              #{index + 1}
            </div>
          </div>

          {/* Memory Text */}
          <div className="mb-4">
            <p className="text-gray-700 leading-relaxed whitespace-pre-line text-sm break-words overflow-wrap-anywhere">
              {displayText}
            </p>
            
            {/* Show More/Less Button */}
            {shouldTruncate && (
              <button
                onClick={toggleExpanded}
                className={`mt-2 font-medium text-xs px-2 py-1 rounded-full transition-colors duration-200 ${
                  'text-[#b8964f] hover:text-[#96793c] hover:bg-[#faf6ec]'
                }`}
              >
                {isExpanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>

          {/* Footer with interactions */}
          <div className="flex items-center justify-between">
            {/* Treasure button with counter */}
            <button 
              onClick={() => handleTreasureMemory(memory.id)}
              disabled={isTreasuring || !auth.currentUser}
              className={`flex items-center gap-2 px-3 py-2 rounded-full transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed ${
                isUserTreasured
                  ? 'bg-[#faf6ec] text-[#b8964f] hover:bg-[#f3ecdb]'
                  : 'text-gray-400 hover:text-[#b8964f] hover:bg-[#faf6ec]'
              } ${isTreasuring ? 'animate-pulse' : ''}`}
              title={
                !auth.currentUser 
                  ? 'Log in to treasure this memory'
                  : isUserTreasured 
                    ? 'You treasured this memory ❤️'
                    : 'Treasure this memory ❤️'
              }
            >
              <span className={`text-lg transition-transform duration-200 ${
                isUserTreasured ? 'scale-110' : 'hover:scale-110'
              }`}>
                ❤️
              </span>
              <span className="text-xs font-medium">
                {treasureCount > 0 ? (
                  `${treasureCount} ${treasureCount === 1 ? 'person treasures' : 'people treasure'} this memory`
                ) : (
                  'Treasure this memory'
                )}
              </span>
            </button>
            
            {/* Engagement actions */}
            <div className="flex items-center gap-2">
              {/* Report — quiet, but always available (App Store UGC rule) */}
              {auth.currentUser ? (
                <button
                  onClick={submitReport}
                  className={`text-[10px] px-2 py-1 rounded-full transition-colors duration-200 ${
                    reportState === 'sent'
                      ? 'text-gray-400 cursor-default'
                      : reportState === 'confirm'
                        ? 'text-red-500 bg-red-50 hover:bg-red-100'
                        : 'text-gray-300 hover:text-gray-500'
                  }`}
                  title="Report this post to SouleMap"
                >
                  {reportState === 'sent' ? 'Reported ✓' : reportState === 'confirm' ? 'Confirm report?' : 'Report'}
                </button>
              ) : (
                <button
                  onClick={handleLoginRedirect}
                  className="text-[10px] px-2 py-1 rounded-full text-gray-300 hover:text-gray-500 transition-colors duration-200"
                  title="Log in to report this post"
                >
                  Report
                </button>
              )}
              <button
                onClick={toggleComments}
                className={`p-1 transition-colors duration-200 flex items-center gap-1 ${
                  'text-gray-400 hover:text-[#b8964f] hover:bg-[#faf6ec]'
                }`}
              >
                {/* Arrow that changes direction based on comments visibility */}
<svg 
  className="w-4 h-4 transition-transform duration-200" 
  fill="currentColor" 
  viewBox="0 0 20 20"
>
  {isCommentsVisible ? (
    // Down arrow when comments are visible
    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
  ) : (
    // Right arrow when comments are hidden
    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
  )}
</svg>
                {comments.length > 0 && (
                  <span className="text-xs">{comments.length}</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Comments Section - UPDATED with edit/delete functionality */}
      {isCommentsVisible && (
        <div className={`mt-4 pt-4 border-t ${
          'border-[#f3efe6]'
        }`}>
    


{/* Comments Section - UPDATED with edit/delete functionality */}
{isCommentsVisible && (
  <div className={`mt-4 pt-4 border-t ${
    'border-[#f3efe6]'
  }`}>
    
    {/* "Comments" SECTION: */}
    {comments.length > 0 && (
      <div className="space-y-3 mb-4">
        {comments.map(comment => (
          <div key={comment.id} className="flex gap-1 md:gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-500 flex items-center justify-center text-white text-xs font-medium shrink-0">
              {comment.userName?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="bg-gray-50 px-3 py-2 rounded">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-800">
                      {comment.userName || 'Anonymous'}
                    </span>
                    <span className="text-xs text-gray-500">
                      {getRelativeTime(comment.timestamp)}
                    </span>
                    {comment.isEdited && (
                      <span className="text-xs text-gray-400 italic">(edited)</span>
                    )}
                  </div>
                  {/* Edit/Delete buttons */}
                  <div className="flex items-center gap-1">
                    {canUserModifyComment(comment) && (
                      <button
                        onClick={() => handleEditComment(memory.id, comment.id, comment.text)}
                        className="p-1 text-gray-400 hover:text-blue-500 hover:bg-blue-50 transition-colors duration-200 rounded"
                        title="Edit comment"
                      >
                        <span className="text-xs">✏️</span>
                      </button>
                    )}
                    {(canUserModifyComment(comment) || isMemorialOwner()) && (
                      <button
                        onClick={() => handleDeleteComment(memory.id, comment.id)}
                        className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors duration-200 rounded"
                        title="Delete comment"
                      >
                        <span className="text-xs">🗑️</span>
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Comment text with proper wrapping - THIS IS THE NEW PART */}
                {editingComments.has(comment.id) ? (
                  <div className="space-y-2">
                    <textarea
                      value={editCommentTexts[comment.id] || ''}
                      onChange={(e) => {
                        const newEditTexts = { ...editCommentTexts };
                        newEditTexts[comment.id] = e.target.value;
                        setEditCommentTexts(newEditTexts);
                      }}
                      className="w-full px-2 py-1 text-sm border border-gray-200 focus:ring-2 focus:ring-blue-300 focus:border-transparent resize-none rounded"
                      style={{ fontSize: '16px' }} // Prevents mobile zoom
                      rows="2"
                      autoFocus
                    />
                    <div className="flex gap-1 md:gap-3">
                      <button
                        onClick={() => handleSaveComment(memory.id, comment.id)}
                        className="sm-btn-small"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => handleCancelEdit(comment.id)}
                        className="px-2 py-1 bg-gray-300 text-gray-700 text-xs hover:bg-gray-400 transition-colors rounded"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-700 leading-relaxed break-words overflow-wrap-anywhere">
                    {comment.text}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    )}

    {/* ALSO REPLACE THE "Add Comment" SECTION WITH: */}
    {auth.currentUser ? (
      <div className="flex gap-1 md:gap-3">
        <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-green-500 flex items-center justify-center text-white text-xs font-medium shrink-0">
          {getUserDisplayName(auth.currentUser).charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-col gap-2">
            <textarea
              key={`comment-input-${memory.id}`}
              rows={2}
              placeholder="Add a comment..."
              value={commentTexts[memory.id] || ''}
              onChange={(e) => {
                const newTexts = { ...commentTexts };
                newTexts[memory.id] = e.target.value;
                setCommentTexts(newTexts);
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !submittingComments[memory.id] && commentTexts[memory.id]?.trim()) {
                  e.preventDefault();
                  handleSubmitComment(memory.id);
                }
              }}
              className="w-full px-4 py-3 bg-[#faf9f7] border border-[#eae6dd] text-base focus:outline-none focus:border-[#cbab6a] resize-none rounded-xl transition-colors"
              style={{ 
                fontSize: '16px', // Prevents mobile zoom
                minHeight: '44px' // Ensures minimum touch target size
              }}
              disabled={submittingComments[memory.id]}
              autoComplete="off"
            />
            <div className="flex justify-end">
              <button
                onClick={() => handleSubmitComment(memory.id)}
                disabled={!commentTexts[memory.id]?.trim() || submittingComments[memory.id]}
                className="sm-btn-primary"
              >
                {submittingComments[memory.id] ? 'Posting...' : 'Post'}
              </button>
            </div>
          </div>
        </div>
      </div>
    ) : (
      <div className="text-center py-3">
        <button
          onClick={handleLoginRedirect}
          className={`font-medium text-xs px-3 py-1 rounded-full transition-colors duration-200 ${
            'text-[#b8964f] hover:text-[#96793c] hover:bg-[#faf6ec]'
          }`}
        >
          Log in to comment
        </button>
      </div>
    )}
  </div>
)}  



  </div>
      )}
    </div>
  );
}

// NEW: Favorites Section Component
const FavoritesSection = ({ favorites, memorial }) => {
  // Initialize with all categories expanded by default
  const [expandedCategories, setExpandedCategories] = useState(() => {
    const allCategories = favorites.reduce((acc, favorite) => {
      const category = favorite.category || 'other';
      acc.add(category);
      return acc;
    }, new Set());
    return allCategories;
  });
  const [playingVideos, setPlayingVideos] = useState(new Set());

  const favoriteCategories = {
    music: { label: 'Music & Songs', emoji: '🎵' },
    places: { label: 'Places', emoji: '' },
    foods: { label: 'Foods & Recipes', emoji: '' },
    books: { label: 'Books & Authors', emoji: '' },
    movies: { label: 'Movies & Shows', emoji: '' },
    hobbies: { label: 'Hobbies & Activities', emoji: '' },
    sports: { label: 'Sports & Teams', emoji: '' },
    quotes: { label: 'Quotes & Sayings', emoji: '' },
    memories: { label: 'Special Memories', emoji: '' },
    other: { label: 'Other', emoji: '' }
  };

  // Group favorites by category
  const groupedFavorites = favorites.reduce((acc, favorite) => {
    const category = favorite.category || 'other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(favorite);
    return acc;
  }, {});

  const toggleCategory = (category) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const handlePlayVideo = (favoriteId) => {
    const newPlaying = new Set(playingVideos);
    if (newPlaying.has(favoriteId)) {
      newPlaying.delete(favoriteId);
    } else {
      newPlaying.add(favoriteId);
    }
    setPlayingVideos(newPlaying);
  };

  if (!favorites || favorites.length === 0) {
    return null;
  }

  return (
    <div className="bg-white p-6 md:p-10 mb-8 border border-[#eee9df]">
      <div className="text-center mb-8">
        <p className="text-[10.5px] uppercase" style={{ letterSpacing: '0.34em', color: '#b8964f' }}>
          What They Loved
        </p>
        <h2 className="mt-3 text-2xl md:text-3xl font-extralight text-gray-900" style={{ letterSpacing: '0.04em' }}>
          Favorites & Passions
        </h2>
        <div
          className="mx-auto mt-4 mb-4"
          style={{ width: '54px', height: '1px', background: 'linear-gradient(90deg, transparent, #cbab6a, transparent)' }}
        />
        <p className="text-gray-500 text-sm font-light">
          Celebrating what brought joy to their life
        </p>
      </div>

      <div className="space-y-6">
        {Object.entries(groupedFavorites).map(([category, items]) => {
          const isExpanded = expandedCategories.has(category);
          const categoryInfo = favoriteCategories[category] || favoriteCategories.other;
          
          return (
            <div
              key={category}
              className="bg-white border border-[#eae6dd] rounded-2xl overflow-hidden transition-colors duration-200 hover:border-[#d8c9a4]"
            >
              <button
                onClick={() => toggleCategory(category)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-[#faf9f7] transition-colors duration-200"
              >
                <div className="flex items-center gap-3">
                  {categoryInfo.emoji && (
                    <span className="text-2xl">{categoryInfo.emoji}</span>
                  )}
                  <h3 className="text-lg font-light text-gray-800" style={{ letterSpacing: '0.03em' }}>
                    {categoryInfo.label}
                  </h3>
                  <span className="text-sm" style={{ color: '#b8964f' }}>({items.length})</span>
                </div>
                <svg
                  className={`w-5 h-5 text-[#cbab6a] transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isExpanded && (
                <div className="border-t border-[#eee9df]">
                  <div className="p-6 space-y-4">
                    {items.map((favorite, index) => (
                      <div
                        key={favorite.id || index}
                        className="bg-[#fdfbf6] p-4 rounded-xl border border-[#eae6dd] hover:shadow-[0_4px_18px_rgba(184,150,79,0.12)] transition-shadow duration-200"
                      >
                        {/* Only show title if there's no YouTube video */}
                        {(!favorite.youtubeUrl || !getYouTubeVideoId(favorite.youtubeUrl)) && (
                          <h4 className="font-medium text-gray-800 mb-2">
                            {favorite.title}
                          </h4>
                        )}
                        
                        {favorite.description && (
                          <p className="text-sm text-gray-600 leading-relaxed mb-3">
                            {favorite.description}
                          </p>
                        )}

                      {/* YouTube Video with Memorial Tribute Style Thumbnail */}
                        {favorite.youtubeUrl && getYouTubeVideoId(favorite.youtubeUrl) && (
                          <div className="mt-4">
                            {playingVideos.has(favorite.id) ? (
                              // Video Player
                              <div className="relative aspect-video shadow-xl">
                                <iframe
                                  src={`https://www.youtube.com/embed/${getYouTubeVideoId(favorite.youtubeUrl)}?autoplay=1&rel=0&modestbranding=1`}
                                  title={`Video for ${favorite.title}`}
                                  className="w-full h-full"
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                ></iframe>
                                <button
                                  onClick={() => handlePlayVideo(favorite.id)}
                                  className="absolute top-2 right-2 bg-black/70 text-white p-2 hover:bg-black/80 transition-colors"
                                  title="Close video"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            ) : (
                              // Custom Thumbnail exactly matching Memorial Tribute Video style
                              <div 
                                className="relative cursor-pointer group overflow-hidden aspect-video rounded-2xl border border-[#cbab6a] shadow-xl"
                                onClick={() => handlePlayVideo(favorite.id)}
                              >
                                {/* Night sky */}
                                <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 20%, #1a1530 0%, #05040d 80%)' }}>
                    <div className="absolute top-[18%] left-[12%] w-1 h-1 rounded-full bg-[#ffe2a8] opacity-60 animate-pulse"></div>
                    <div className="absolute top-[30%] right-[18%] w-1 h-1 rounded-full bg-[#c9d4ef] opacity-50 animate-pulse delay-300"></div>
                    <div className="absolute bottom-[24%] left-[28%] w-0.5 h-0.5 rounded-full bg-[#c9d4ef] opacity-60"></div>
                    <div className="absolute top-[12%] right-[38%] w-0.5 h-0.5 rounded-full bg-[#ffe2a8] opacity-40"></div>
                    <div className="absolute bottom-[16%] right-[26%] w-1 h-1 rounded-full bg-[#ffe2a8] opacity-50 animate-pulse delay-700"></div>
                    <div className="absolute top-[45%] left-[40%] w-0.5 h-0.5 rounded-full bg-[#c9d4ef] opacity-50"></div>
                  </div>
                                
                                {/* Memorial Image Overlay - Same as Memorial Tribute Video */}
                                {memorial.images?.[0] && (
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="relative">
                                      <img
                                        src={memorial.images[0]}
                                        alt={`${memorial.firstName} ${memorial.lastName}`}
                                        className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover transition-transform duration-500 group-hover:scale-105 shadow-[0_0_0_2px_rgba(255,255,255,0.9),0_0_0_3px_rgba(203,171,106,0.7),0_0_40px_rgba(255,210,130,0.45)]"
                                        onError={e => (e.target.style.display = 'none')}
                                      />
                                      {/* Heavenly glow around image */}
                                      <div className="absolute -inset-3 rounded-full border border-[#e5c883]/50 animate-pulse"></div>
                                      <div className="absolute -inset-6 rounded-full border border-[#e5c883]/20 animate-pulse delay-300"></div>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Play Button */}
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="bg-black/40 backdrop-blur-sm border border-[#e5c883]/70 rounded-full p-6 shadow-xl transition-all duration-300 group-hover:bg-black/60 group-hover:scale-110">
                                    <div className="w-0 h-0 border-l-[20px] border-l-[#ffe9b8] border-t-[14px] border-t-transparent border-b-[14px] border-b-transparent ml-1 drop-shadow-lg"></div>
                                  </div>
                                </div>
                                
                                {/* Video Title Overlay */}
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 via-black/40 to-transparent p-6">
                                  <h3 className="text-white text-lg md:text-xl font-light text-center drop-shadow-lg">
                                    {favorite.title}
                                  </h3>
                                  <p className="text-white/80 text-sm text-center mt-1 drop-shadow">
                                    Click to play video
                                  </p>
                                </div>
                                
                                {/* Hover Effects */}
                                <div className="absolute inset-0 bg-gradient-to-br from-[#e5c883]/0 via-transparent to-[#e5c883]/0 group-hover:from-[#e5c883]/10 group-hover:to-[#e5c883]/5 transition-all duration-500"></div>
                              </div>
                            )}
                          </div>
                        )}
                        {/* Special styling for quotes */}
                        {category === 'quotes' && (
                          <div className="mt-3 pl-4 italic text-gray-600 font-light" style={{ borderLeft: '2px solid #cbab6a' }}>
                            "{favorite.title}"
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
// Subscriber Badge Component - Three roses for premium users, "Subscribe to verify" button for others
const SubscriberBadge = ({ isActive, isLoading }) => {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-200/50 text-gray-400 text-xs rounded-full animate-pulse">
        <span className="w-3 h-3 bg-gray-300 rounded-full"></span>
      </span>
    );
  }
  
  if (!isActive) {
    return (
      <button
        onClick={() => navigate('/subscription')}
        className="inline-flex items-center text-xs text-gray-500 bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded-full transition-colors duration-200 cursor-pointer"
        title="Click to subscribe and verify this memorial"
      >
        Subscribe to verify
      </button>
    );
  }
  
  return (
    <span 
      className="inline-flex items-center"
      title="Premium Memorial - Ad-free experience with enhanced features"
    >
      <span className="text-lg">🌹🌹🌹</span>
  <span className="text-xs text-gray-400 font-light">- verified</span>
    </span>
  );
};
export default function MemorialPage() {
  const { slug } = useParams(); // Changed from id to slug
  const navigate = useNavigate();
  const [memorial, setMemorial] = useState(null);
  const [memorialId, setMemorialId] = useState(null); // Store the actual Firebase document ID
  const [showFullBio, setShowFullBio] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Flower and Card counts for navigation buttons (data managed by components)
  const [flowers, setFlowers] = useState([]);
  const [cards, setCards] = useState([]);
  
  // Video state
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  
  // Donation modal state (kept in parent for action bar button)
  const [showDonateQR, setShowDonateQR] = useState(false);
  const [donationActiveTab, setDonationActiveTab] = useState('donate');
  
  // Memory Wall state variables
  const [memories, setMemories] = useState([]);
  const [memoryText, setMemoryText] = useState('');
  const [memoryPrompt, setMemoryPrompt] = useState('');
  const [submittingMemory, setSubmittingMemory] = useState(false);
  const [expandedMemories, setExpandedMemories] = useState(new Set());
  const [displayedMemoryCount, setDisplayedMemoryCount] = useState(2);
  const [memoryComments, setMemoryComments] = useState({});
  const [commentTexts, setCommentTexts] = useState({});
  const [submittingComments, setSubmittingComments] = useState({});
  const [showComments, setShowComments] = useState(new Set());
  
  // NEW: Comment editing state variables
  const [editingComments, setEditingComments] = useState(new Set());
  const [editCommentTexts, setEditCommentTexts] = useState({});
  
  // Treasure system state variables
  const [treasureCounts, setTreasureCounts] = useState({});
  const [userTreasures, setUserTreasures] = useState(new Set());
  const [treasuringMemories, setTreasuringMemories] = useState(new Set());

  // Floating buttons ("Their Star", "Share") stay hidden until the reader
  // scrolls past the hero — the portrait deserves an uncluttered first look.
  const [showFloating, setShowFloating] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowFloating(window.scrollY > 480);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  
  // Creator information state
  const [creatorInfo, setCreatorInfo] = useState(null);
  
  // NEW: Subscription checking state variables
  const [creatorSubscription, setCreatorSubscription] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  // NEW: Follow memorial state variables
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);

  // NEW: Modal mode state and functionality
  const [isModalMode, _setIsModalMode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('modal') === 'true';
  });

  // NEW: Function to handle closing modal and returning to map
  const handleCloseModal = useCallback(() => {
    if (!isModalMode) return;
    
    // Get all the return parameters from URL
    const params = new URLSearchParams(window.location.search);
    const returnParams = new URLSearchParams();
    
    // Extract all return parameters
    Array.from(params.entries()).forEach(([key, value]) => {
      if (key.startsWith('return') || key === 'modal') {
        returnParams.set(key, value);
      }
    });
    
    // Navigate back to map with all preserved state
    const mapUrl = `/?${returnParams.toString()}`;
    window.location.href = mapUrl; // Use window.location.href for full page reload to ensure proper state restoration
  }, [isModalMode]);

  // NEW: Add modal body class when in modal mode
  useEffect(() => {
    if (isModalMode) {
      // Hide navigation or any other elements you want to hide in modal mode
      document.body.classList.add('modal-memorial-view');
    }
    
    return () => {
      document.body.classList.remove('modal-memorial-view');
    };
  }, [isModalMode]);

  // Dynamic SEO meta tags for memorial page
  useEffect(() => {
    if (!memorial) return;

    const fullName = `${capitalizeWords(memorial.prefix || '')} ${capitalizeWords(memorial.firstName)} ${capitalizeWords(memorial.lastName)}`.trim();
    const birthYear = parseDateParts(memorial.birthDate)?.y || '';
    const deathYear = parseDateParts(memorial.deathDate)?.y || '';
    const lifeSpan = birthYear && deathYear ? `(${birthYear} - ${deathYear})` : '';
    
    const truncatedBio = memorial.bio?.length > 155 
      ? memorial.bio.substring(0, 155) + '...' 
      : memorial.bio || `Remembering and honoring the life of ${fullName}.`;

    // Page Title
    document.title = `${fullName} ${lifeSpan} | Memorial | SouleMap`;

    // Meta Description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = truncatedBio;

    // Meta Keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.content = `${fullName}, memorial, obituary, remembrance, tribute, ${memorial.address || ''}, SouleMap`.trim();

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `https://soulemap.com/memorial/${slug}`;

    // Open Graph tags for social sharing
    const ogTags = [
      { property: 'og:title', content: `${fullName} - Memorial Tribute` },
      { property: 'og:description', content: truncatedBio },
      { property: 'og:type', content: 'profile' },
      { property: 'og:url', content: `https://soulemap.com/memorial/${slug}` },
      { property: 'og:site_name', content: 'SouleMap' },
      { property: 'og:image', content: memorial.images?.[0] || memorial.profilePhoto || 'https://soulemap.com/default-memorial.jpg' },
      { property: 'profile:first_name', content: capitalizeWords(memorial.firstName) },
      { property: 'profile:last_name', content: capitalizeWords(memorial.lastName) },
    ];

    ogTags.forEach(tag => {
      let ogTag = document.querySelector(`meta[property="${tag.property}"]`);
      if (!ogTag) {
        ogTag = document.createElement('meta');
        ogTag.setAttribute('property', tag.property);
        document.head.appendChild(ogTag);
      }
      ogTag.content = tag.content;
    });

    // Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: `${fullName} - Memorial Tribute` },
      { name: 'twitter:description', content: truncatedBio },
      { name: 'twitter:image', content: memorial.images?.[0] || memorial.profilePhoto || 'https://soulemap.com/default-memorial.jpg' },
    ];

    twitterTags.forEach(tag => {
      let twitterTag = document.querySelector(`meta[name="${tag.name}"]`);
      if (!twitterTag) {
        twitterTag = document.createElement('meta');
        twitterTag.name = tag.name;
        document.head.appendChild(twitterTag);
      }
      twitterTag.content = tag.content;
    });

    // Structured Data (JSON-LD) for Person schema - great for search engines
    let jsonLd = document.querySelector('script[type="application/ld+json"]#memorial-schema');
    if (!jsonLd) {
      jsonLd = document.createElement('script');
      jsonLd.type = 'application/ld+json';
      jsonLd.id = 'memorial-schema';
      document.head.appendChild(jsonLd);
    }
    
    jsonLd.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Person",
      "name": fullName,
      "givenName": capitalizeWords(memorial.firstName),
      "familyName": capitalizeWords(memorial.lastName),
      "birthDate": memorial.birthDate || undefined,
      "deathDate": memorial.deathDate || undefined,
      "description": memorial.bio || undefined,
      "image": memorial.images?.[0] || memorial.profilePhoto || undefined,
      "url": `https://soulemap.com/memorial/${slug}`,
      "sameAs": `https://soulemap.com/memorial/${slug}`,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://soulemap.com/memorial/${slug}`,
        "name": `${fullName} - Memorial`,
        "description": truncatedBio,
        "isPartOf": {
          "@type": "WebSite",
          "name": "SouleMap",
          "url": "https://soulemap.com"
        }
      }
    });

    return () => {
      document.title = 'SouleMap';
    };
  }, [memorial, slug]);

  // Helper function to get user display name
  const getUserDisplayName = (user) => {
    if (!user) return 'Anonymous';
    return user.displayName || user.email?.split('@')[0] || 'Anonymous';
  };

  // Helper function to get relative time
  const getRelativeTime = (timestamp) => {
    const now = new Date();
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffInMs = now - date;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  // Helper function to handle login redirect
  const handleLoginRedirect = () => {
    const currentUrl = window.location.pathname + window.location.search;
    const loginUrl = `/login?redirect=${encodeURIComponent(currentUrl)}`;
    window.location.href = loginUrl;
  };

  // Follow/Unfollow memorial functions
  const handleFollowToggle = async () => {
    if (!auth.currentUser) {
      handleLoginRedirect();
      return;
    }
    
    if (!memorialId) return;
    
    setFollowLoading(true);
    try {
      const followRef = doc(db, 'users', auth.currentUser.uid, 'following', memorialId);
      const followerRef = doc(db, 'memorials', memorialId, 'followers', auth.currentUser.uid);
      
      if (isFollowing) {
        // Unfollow
        await deleteDoc(followRef);
        await deleteDoc(followerRef);
        setIsFollowing(false);
        setFollowerCount(prev => Math.max(0, prev - 1));
      } else {
        // Follow
        const followData = {
          memorialId: memorialId,
          memorialSlug: slug,
          memorialName: `${memorial.firstName} ${memorial.lastName}`,
          memorialPhoto: memorial.profilePhoto || null,
          followedAt: new Date(),
          lastSeenAt: new Date()
        };
        
        const followerData = {
          userId: auth.currentUser.uid,
          userEmail: auth.currentUser.email,
          userName: auth.currentUser.displayName || auth.currentUser.email?.split('@')[0],
          followedAt: new Date()
        };
        
        await setDoc(followRef, followData);
        await setDoc(followerRef, followerData);
        setIsFollowing(true);
        setFollowerCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
    } finally {
      setFollowLoading(false);
    }
  };

  // Check follow status and count when memorial loads
  useEffect(() => {
    if (!memorialId) return;
    
    // Get follower count
    const followersRef = collection(db, 'memorials', memorialId, 'followers');
    const unsubFollowers = onSnapshot(followersRef, (snapshot) => {
      setFollowerCount(snapshot.size);
    });
    
    // Check if current user is following
    if (auth.currentUser) {
      const followRef = doc(db, 'users', auth.currentUser.uid, 'following', memorialId);
      const unsubFollow = onSnapshot(followRef, (snapshot) => {
        setIsFollowing(snapshot.exists());
      });
      
      return () => {
        unsubFollowers();
        unsubFollow();
      };
    }
    
    return () => unsubFollowers();
  }, [memorialId, auth.currentUser]);

  // Scroll to section functions
  const scrollToFlowersSection = () => {
    const element = document.getElementById('flowers-section');
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest'
      });
      // Add a subtle highlight effect
      element.classList.add('animate-pulse');
      setTimeout(() => element.classList.remove('animate-pulse'), 2000);
    }
  };

  const scrollToCardsSection = () => {
    const element = document.getElementById('cards-section');
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest'
      });
      // Add a subtle highlight effect
      element.classList.add('animate-pulse');
      setTimeout(() => element.classList.remove('animate-pulse'), 2000);
    }
  };

  const scrollToMemoryWall = () => {
    const element = document.getElementById('memory-wall-section');
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest'
      });
      // Add a subtle highlight effect
      element.classList.add('animate-pulse');
      setTimeout(() => element.classList.remove('animate-pulse'), 2000);
    }
  };

  // NEW: Comment editing functions
  const handleEditComment = (memoryId, commentId, currentText) => {
    const newEditing = new Set(editingComments);
    newEditing.add(commentId);
    setEditingComments(newEditing);
    
    const newEditTexts = { ...editCommentTexts };
    newEditTexts[commentId] = currentText;
    setEditCommentTexts(newEditTexts);
  };

  const handleSaveComment = async (memoryId, commentId) => {
    const newText = editCommentTexts[commentId]?.trim();
    if (!newText || !auth.currentUser) return;

    try {
      await updateDoc(doc(db, 'memorials', memorialId, 'memories', memoryId, 'comments', commentId), {
        text: newText,
        isEdited: true,
        editedAt: new Date()
      });
      
      // Clear editing state
      handleCancelEdit(commentId);
    } catch (error) {
      console.error('Error updating comment:', error);
      alert('Error updating comment. Please try again.');
    }
  };

  const handleCancelEdit = (commentId) => {
    const newEditing = new Set(editingComments);
    newEditing.delete(commentId);
    setEditingComments(newEditing);
    
    const newEditTexts = { ...editCommentTexts };
    delete newEditTexts[commentId];
    setEditCommentTexts(newEditTexts);
  };

  // Delete comment function
  const handleDeleteComment = async (memoryId, commentId) => {
    if (!auth.currentUser) {
      alert('Please log in to delete comments.');
      return;
    }

    // Check if user can delete this comment
    const comment = memoryComments[memoryId]?.find(c => c.id === commentId);
    const canDelete = comment && (
      comment.userId === auth.currentUser.uid || // User owns the comment
      memorial.createdBy === auth.currentUser.uid // User owns the memorial
    );

    if (!canDelete) {
      alert('You can only delete your own comments.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this comment? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'memorials', memorialId, 'memories', memoryId, 'comments', commentId));
      // Success feedback - comment will be removed automatically by the real-time listener
    } catch (error) {
      console.error('Error deleting comment:', error);
      alert('Error deleting comment. Please try again.');
    }
  };

  // Check if current user is memorial owner
  const isMemorialOwner = () => {
    return auth.currentUser && memorial && memorial.createdBy === auth.currentUser.uid;
  };

  // Forever (pre-need): a living memorial, not yet activated
  const isLiving = !!(memorial?.preNeed && memorial?.preNeedStatus === 'living');
  // Families hold their history collectively: the owner, any Forever steward,
  // and everyone invited under "Family & Friends" may build the family tree.
  const canEditFamilyTree = !!(
    auth.currentUser && memorial && (
      memorial.createdBy === auth.currentUser.uid ||
      (auth.currentUser.email && Array.isArray(memorial.relatives) &&
        memorial.relatives.includes(auth.currentUser.email.toLowerCase())) ||
      (auth.currentUser.email && Array.isArray(memorial.stewardEmails) &&
        memorial.stewardEmails.includes(auth.currentUser.email.toLowerCase()))
    )
  );

  const isSteward = !!(
    auth.currentUser?.email &&
    Array.isArray(memorial?.stewardEmails) &&
    memorial.stewardEmails.includes(auth.currentUser.email.toLowerCase())
  );

  // NEW: Check if donations are enabled based on creator's subscription
  // This replaces the old memorial.donationsEnabled field check
  const isDonationsEnabled = () => {
    if (checkingSubscription) return false; // Don't show donations while checking
    
    // Check creator subscription
    const hasActiveSubscription = creatorSubscription && isSubscriptionActive(creatorSubscription);
    if (!hasActiveSubscription) return false;
    
    // Check memorial's donationEnabled field (default to true if not present)
    const memorialDonationsEnabled = memorial?.donationEnabled !== undefined 
      ? memorial.donationEnabled 
      : true; // Default to true for backward compatibility
    
    return memorialDonationsEnabled;
  };

  // Handle memory submission
  const handleSubmitMemory = async () => {
    if (!auth.currentUser) {
      alert('Please log in to share a memory.');
      handleLoginRedirect();
      return;
    }
    
    if (!memoryText.trim()) {
      alert('Please write a memory before submitting.');
      return;
    }

    setSubmittingMemory(true);
    try {
      await addDoc(collection(db, 'memorials', memorialId, 'memories'), {
        userName: getUserDisplayName(auth.currentUser),
        userId: auth.currentUser.uid,
        text: memoryText.trim(),
        timestamp: new Date(),
        treasureCount: 0, // Initialize treasure count
      });
      events.memoryPosted();
      setMemoryText('');
      // Success feedback could be added here
    } catch (error) {
      console.error('Error submitting memory:', error);
      alert('Error submitting memory. Please try again.');
    } finally {
      setSubmittingMemory(false);
    }
  };

  // Handle comment submission
  const handleSubmitComment = async (memoryId) => {
    if (!auth.currentUser) {
      alert('Please log in to comment.');
      handleLoginRedirect();
      return;
    }
    
    const commentText = commentTexts[memoryId]?.trim();
    if (!commentText) {
      alert('Please write a comment before submitting.');
      return;
    }

    setSubmittingComments(prev => ({ ...prev, [memoryId]: true }));
    try {
      await addDoc(collection(db, 'memorials', memorialId, 'memories', memoryId, 'comments'), {
        userName: getUserDisplayName(auth.currentUser),
        userId: auth.currentUser.uid,
        text: commentText,
        timestamp: new Date(),
        isEdited: false
      });
      setCommentTexts(prev => ({ ...prev, [memoryId]: '' }));
    } catch (error) {
      console.error('Error submitting comment:', error);
      alert('Error submitting comment. Please try again.');
    } finally {
      setSubmittingComments(prev => ({ ...prev, [memoryId]: false }));
    }
  };

  // Handle treasure/untreasure memory
  const handleTreasureMemory = async (memoryId) => {
    if (!auth.currentUser) {
      alert('Please log in to treasure memories.');
      handleLoginRedirect();
      return;
    }

    const treasureId = `${auth.currentUser.uid}_${memoryId}`;
    const isCurrentlyTreasured = userTreasures.has(memoryId);
    
    setTreasuringMemories(prev => new Set([...prev, memoryId]));

    try {
      if (isCurrentlyTreasured) {
        // Remove treasure
        await deleteDoc(doc(db, 'memorials', memorialId, 'treasures', treasureId));
        await updateDoc(doc(db, 'memorials', memorialId, 'memories', memoryId), {
          treasureCount: increment(-1)
        });
      } else {
        // Add treasure
        await setDoc(doc(db, 'memorials', memorialId, 'treasures', treasureId), {
          userId: auth.currentUser.uid,
          memoryId: memoryId,
          timestamp: new Date(),
        });
        await updateDoc(doc(db, 'memorials', memorialId, 'memories', memoryId), {
          treasureCount: increment(1)
        });
      }
    } catch (error) {
      console.error('Error treasuring memory:', error);
      alert('Error treasuring memory. Please try again.');
    } finally {
      setTreasuringMemories(prev => {
        const newSet = new Set(prev);
        newSet.delete(memoryId);
        return newSet;
      });
    }
  };

  // Video handlers
  const handleVideoPlay = () => {
    setIsVideoPlaying(true);
  };

  // Temporary: Log user UID for Firebase setup
  useEffect(() => {
    if (auth.currentUser) {
    }
  }, []);

  // Get creator name helper function
  const getCreatorName = () => {
    if (!memorial) return 'Loading...';
    
    // If current user is the creator, show their full name
    if (auth.currentUser && memorial.createdBy === auth.currentUser.uid) {
      if (auth.currentUser.displayName) {
        return auth.currentUser.displayName;
      }
      if (auth.currentUser.email) {
        return formatEmailToName(auth.currentUser.email);
      }
      return 'You (Memorial Creator)';
    }
    
    // For other users, try multiple sources in order of preference
    
    // 1. First try fetched creator info
    if (creatorInfo?.displayName) {
      return creatorInfo.displayName;
    }
    
    // 2. Try stored creator info in memorial document (best fallback)
    if (memorial.creatorDisplayName) {
      return memorial.creatorDisplayName;
    }
    
    if (memorial.creatorName) {
      return memorial.creatorName;
    }
    
    if (memorial.createdByName) {
      return memorial.createdByName;
    }
    
    // 3. Try to format email from fetched creator info
    if (creatorInfo?.email) {
      return formatEmailToName(creatorInfo.email);
    }
    
    // 4. Try to format email from memorial document
    if (memorial.creatorEmail) {
      return formatEmailToName(memorial.creatorEmail);
    }
    
    // 5. Last resort - try to get from auth user who created it
    if (memorial.createdBy) {
      // This will be populated by the useEffect below
      return 'Loading creator info...';
    }
    
    return 'Memorial Creator';
  };

  // Fetch memorial data by slug instead of ID
  useEffect(() => {
    async function fetchMemorialData() {
      if (!slug) return;
       
      try {
        
        
        // Query memorials collection by slug field
        const q = query(
          collection(db, 'memorials'),
          where('slug', '==', slug)
        );
        
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const memorialDoc = querySnapshot.docs[0];
          const memorialData = memorialDoc.data();
          const documentId = memorialDoc.id;
          
          
          
          
          // Debug: Log image URLs to check format
          if (memorialData.images) {
            
            memorialData.images.forEach((img, idx) => {
              
            });
          }
          if (memorialData.bannerImage) {
            
          }
          
          setMemorial(memorialData);
          setMemorialId(documentId); // Store the actual Firebase document ID
          setLoading(false);
          
          // Fetch creator info in background (non-blocking)
          if (memorialData.createdBy && (!auth.currentUser || memorialData.createdBy !== auth.currentUser.uid)) {
            fetchCreatorInfo(memorialData).catch(console.error);
          } else if (memorialData.createdBy === auth.currentUser?.uid) {
            
          }
        } else {
          
          setMemorial(null);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching memorial:', error);
        setMemorial(null);
        setLoading(false);
      }
    }

    // Helper function to fetch creator info asynchronously
    async function fetchCreatorInfo(memorialData) {
      try {
        
        
        
        // Use data stored in memorial document
        setCreatorInfo({
          displayName: memorialData.creatorDisplayName || 
                      memorialData.creatorName || 
                      memorialData.createdByName ||
                      (memorialData.creatorEmail ? formatEmailToName(memorialData.creatorEmail) : null),
          email: memorialData.creatorEmail
        });
        
      } catch (error) {
        
        // Fallback to basic memorial stored data
        setCreatorInfo({
          displayName: memorialData.creatorDisplayName || 
                      memorialData.creatorName || 
                      memorialData.createdByName ||
                      (memorialData.creatorEmail ? formatEmailToName(memorialData.creatorEmail) : null),
          email: memorialData.creatorEmail
        });
      }
    }

    fetchMemorialData();
  }, [slug]);

  // Add this useEffect hook in your MemorialPage component after the memorial data is loaded
  useEffect(() => {
    if (memorial && slug) {
      // Update meta tags for client-side navigation
      const metaTags = MetaTagManager.generateMemorialMetaTags(memorial, slug);
      MetaTagManager.updateMetaTags(metaTags);
      
      
    }
  }, [memorial, slug]);

  // Also add a cleanup effect to reset meta tags when leaving the page
  useEffect(() => {
    return () => {
      // Reset to default meta tags when component unmounts
      MetaTagManager.resetToDefault();
    };
  }, []);

  // NEW: Check memorial creator's subscription status
  useEffect(() => {
    const checkCreatorSubscription = async () => {
      if (!memorial || !memorial.createdBy) {
        setCheckingSubscription(false);
        return;
      }
      
      try {

        const subscription = await getUserSubscription(memorial.createdBy);
        setCreatorSubscription(subscription);
        
      } catch (error) {
        console.error('💥 Error checking creator subscription:', error);
      } finally {
        setCheckingSubscription(false);
      }
    };

    if (memorial) {
      checkCreatorSubscription();
    }
  }, [memorial]);

  // Subscribe to flowers count for navigation display
  useEffect(() => {
    if (!memorialId || !memorial) return;
    
    const q = query(
      collection(db, 'memorials', memorialId, 'flowers'),
      orderBy('timestamp', 'desc')
    );
    const unsubscribe = onSnapshot(q, snapshot => {
      setFlowers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, console.error);
    return () => unsubscribe();
  }, [memorialId, memorial]);

  // Subscribe to cards count for navigation display
  useEffect(() => {
    if (!memorialId || !memorial) return;
    
    const q = query(
      collection(db, 'memorials', memorialId, 'cards'),
      orderBy('timestamp', 'desc')
    );
    const unsubscribe = onSnapshot(q, snapshot => {
      setCards(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, console.error);
    return () => unsubscribe();
  }, [memorialId, memorial]);

  // Optimized: Lazy load memories and comments
  useEffect(() => {
    if (!memorialId || !memorial) return; // Wait for memorial first
    
    const q = query(
      collection(db, 'memorials', memorialId, 'memories'),
      orderBy('timestamp', 'desc')
    );
    const unsubscribe = onSnapshot(q, snapshot => {
      const memoriesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMemories(memoriesData);
      
      // Extract treasure counts
      const counts = {};
      memoriesData.forEach(memory => {
        counts[memory.id] = memory.treasureCount || 0;
      });
      setTreasureCounts(counts);
      
      // Lazy load comments only for first 5 memories initially to improve performance
      const initialMemoriesToLoad = memoriesData.slice(0, 5);
      initialMemoriesToLoad.forEach(memory => {
        const commentsQuery = query(
          collection(db, 'memorials', memorialId, 'memories', memory.id, 'comments'),
          orderBy('timestamp', 'asc')
        );
        onSnapshot(commentsQuery, commentsSnapshot => {
          const comments = commentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setMemoryComments(prev => ({
            ...prev,
            [memory.id]: comments
          }));
        });
      });
    }, console.error);
    return () => unsubscribe();
  }, [memorialId, memorial, displayedMemoryCount]);

  // Optimized: Subscribe to user's treasures only when user is authenticated
  useEffect(() => {
    if (!memorialId || !auth.currentUser || !memorial) {
      setUserTreasures(new Set());
      return;
    }
    
    const q = query(
      collection(db, 'memorials', memorialId, 'treasures'),
      orderBy('timestamp', 'desc')
    );
    const unsubscribe = onSnapshot(q, snapshot => {
      const userTreasureSet = new Set();
      snapshot.docs.forEach(doc => {
        const treasure = doc.data();
        if (treasure.userId === auth.currentUser.uid) {
          userTreasureSet.add(treasure.memoryId);
        }
      });
      setUserTreasures(userTreasureSet);
    }, console.error);
    return () => unsubscribe();
  }, [memorialId, memorial]);

  // ADD this new useEffect for testing backend connection (helpful for debugging)
  useEffect(() => {
    const testBackendConnection = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/test`);
        
        if (!response.ok) {
          console.warn(`⚠️ Backend test failed: HTTP ${response.status}`);
          return false;
        }
        
        const data = await response.json();
        
        
        if (!data.stripe_configured) {
          console.warn('⚠️ Stripe not configured on backend');
        }
        
        const unconfiguredFlowers = Object.entries(data.flower_prices_configured || {})
          .filter(([_, configured]) => !configured)
          .map(([flower, _]) => flower);
          
        if (unconfiguredFlowers.length > 0) {
          console.warn('⚠️ Unconfigured flowers:', unconfiguredFlowers);
        } else {
          
        }
        
        const unconfiguredCards = Object.entries(data.card_prices_configured || {})
          .filter(([_, configured]) => !configured)
          .map(([card, _]) => card);
          
        if (unconfiguredCards.length > 0) {
          console.warn('⚠️ Unconfigured cards:', unconfiguredCards);
        } else {
          
        }

        return true;
      } catch (error) {
        console.error('❌ Backend connection failed:', error);
        return false;
      }
    };

    // Test backend connection on component mount (only in development)
    if (process.env.NODE_ENV === 'development') {
      testBackendConnection();
    }
  }, []);

  // FUTURE DEVELOPMENT: Family Tree Refresh Function
  // const refreshMemorial = async () => { ... };

  // Optimized sorting with useMemo to prevent unnecessary re-calculations
  const sortedMemories = useMemo(() => {
    return [...memories].sort((a, b) => {
      const aTreasures = treasureCounts[a.id] || 0;
      const bTreasures = treasureCounts[b.id] || 0;
      
      // If treasure counts are equal, sort by timestamp (newest first)
      if (aTreasures === bTreasures) {
        const aTime = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
        const bTime = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
        return bTime - aTime;
      }
      
      // Otherwise, sort by treasure count (highest first)
      return bTreasures - aTreasures;
    });
  }, [memories, treasureCounts]);

  // NEW: Get YouTube video ID for embedding with default fallback
  const youtubeVideoId = useMemo(() => {
    // If user provided a video, use that
    if (memorial?.youtubeVideoUrl) {
      return getYouTubeVideoId(memorial.youtubeVideoUrl);
    }
    // Otherwise use default video
    return getYouTubeVideoId("https://youtu.be/1TLANYvPGPE?si=8Bv6iSW7P1dLe2VW");
  }, [memorial?.youtubeVideoUrl]);

  if (loading) {
    return (
      <div
        style={{ fontFamily: 'Garamond, serif' }}
        className="min-h-screen bg-[#faf9f7]"
      >
        {/* Skeleton Header */}
        <div className="relative">
          <div className="h-40 md:h-[240px] bg-gray-200 animate-pulse relative overflow-hidden">
            <div className="absolute top-6 left-8 md:top-12 md:left-12">
              <div className="w-32 h-32 md:w-40 md:h-40 lg:w-48 lg:h-48 bg-gray-300 animate-pulse border-4 border-white"></div>
            </div>
            <div className="absolute top-8 left-32 md:top-24 md:left-60 lg:top-28 lg:left-72 p-4">
              <div className="h-8 bg-gray-300 animate-pulse mb-2 w-64"></div>
              <div className="h-4 bg-gray-300 animate-pulse w-40"></div>
            </div>
          </div>
        </div>

        {/* Skeleton Content */}
        <div className="max-w-4xl mx-auto px-2 md:px-6 -mt-8 relative z-10">
          
{/* Skeleton Action Bar */}
          <div className="bg-white/80 backdrop-blur-lg p-4 md:p-6 mb-8 shadow-xl border border-white/20">
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <div className="h-10 bg-gray-200 animate-pulse w-32"></div>
              <div className="h-10 bg-gray-200 animate-pulse w-32"></div>
              <div className="h-10 bg-gray-200 animate-pulse w-24"></div>
            </div>
          </div>

          {/* Skeleton Bio */}
          <div className="bg-white/70 backdrop-blur-sm p-4 md:p-12 mb-12 border border-white/30">
            <div className="h-8 bg-gray-200 animate-pulse mb-8 w-48 mx-auto"></div>
            <div className="space-y-4">
              <div className="h-4 bg-gray-200 animate-pulse w-full"></div>
              <div className="h-4 bg-gray-200 animate-pulse w-5/6"></div>
              <div className="h-4 bg-gray-200 animate-pulse w-4/5"></div>
            </div>
          </div>

          <div className="text-center">
            <div className="animate-spin h-12 w-12 rounded-full border-b-2 mx-auto mb-4" style={{ borderColor: '#cbab6a' }}></div>
            <p className="text-gray-600 text-lg font-light">Loading memorial...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!memorial) {
    return (
      <div
        style={{ fontFamily: 'Garamond, serif' }}
        className="min-h-screen bg-[#faf9f7] flex items-center justify-center"
      >
        <div className="text-center">
          <div className="text-6xl mb-4">🌹</div>
          <h1 className="text-2xl font-light text-gray-800 mb-2">Memorial Not Found</h1>
          <p className="text-gray-600">This memorial doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{ fontFamily: 'Garamond, serif' }}
      className="min-h-screen bg-[#faf9f7] overflow-x-hidden"
    >
      {/* Add modal CSS */}
      <style>
        {`
          .modal-memorial-view nav,
          .modal-memorial-view header {
            display: none !important;
          }
        `}
      </style>

      {/* Floating Compact Social Share - Both Mobile and Desktop (Bottom Right) */}
      {memorial && (
        <FloatingCompactSocialShare
          memorial={memorial}
          memorialSlug={slug}
          visible={showFloating}
        />
      )}

      {/* Hero Section - UPDATED: Add close button for modal mode and resting place */}
      <div className="relative">
        {/* NEW: Close button for modal mode */}
        {isModalMode && (
          <button
            onClick={handleCloseModal}
            className="fixed top-4 right-4 z-50 w-12 h-12 bg-black/70 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/80 transition-all duration-200 shadow-lg"
            title="Return to map"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        
        {/* Celestial hero — a living night sky with their star */}
        <StarfieldHero />
      </div>

{/* Identity — centered beneath the night sky */}
<div className="relative z-10 -mt-28 md:-mt-32 text-center px-4">
  <div className="relative inline-block">
    <div
      className="absolute -inset-6 rounded-full pointer-events-none"
      style={{
        background: 'radial-gradient(circle, rgba(255,222,150,0.5) 0%, rgba(255,205,120,0.12) 55%, transparent 72%)',
        animation: 'soulBreathe 4.5s ease-in-out infinite',
      }}
    />
    {memorial.images?.[0] ? (
      <img
        src={memorial.images[0]}
        alt={`${memorial.firstName} ${memorial.lastName}`}
        className="relative w-40 h-40 md:w-44 md:h-44 rounded-full object-cover"
        style={{
          border: '3px solid #fff',
          boxShadow: '0 0 0 1px rgba(255,215,140,0.6), 0 0 44px rgba(255,210,130,0.5), 0 18px 40px rgba(0,0,0,0.35)',
          background: '#131832',
        }}
        loading="eager"
        onError={e => { e.target.style.display = 'none'; const fb = e.target.nextElementSibling; if (fb) fb.style.display = 'flex'; }}
      />
    ) : null}
    {/* Default portrait — a night-sky disc with their initials beneath a star */}
    <div
      className="relative w-40 h-40 md:w-44 md:h-44 rounded-full flex-col items-center justify-center"
      style={{
        display: memorial.images?.[0] ? 'none' : 'flex',
        border: '3px solid #fff',
        boxShadow: '0 0 0 1px rgba(255,215,140,0.6), 0 0 44px rgba(255,210,130,0.5), 0 18px 40px rgba(0,0,0,0.35)',
        background: 'radial-gradient(circle at 50% 32%, #232a4d 0%, #131832 55%, #0b0e20 100%)',
      }}
    >
      <span
        className="text-xl"
        style={{ color: '#ffe2a8', textShadow: '0 0 14px rgba(255,215,140,0.8)' }}
      >
        ✦
      </span>
      <span
        className="mt-1 text-4xl md:text-5xl font-extralight"
        style={{ color: '#f5efe2', letterSpacing: '0.08em', fontFamily: 'Garamond, serif' }}
      >
        {(memorial.firstName?.[0] || '').toUpperCase()}{(memorial.lastName?.[0] || '').toUpperCase()}
      </span>
    </div>
  </div>

  <h2
    className="mt-6 text-3xl md:text-4xl font-extralight text-gray-900"
    style={{ letterSpacing: '0.06em' }}
  >
    {capitalizeWords(memorial.prefix)} {capitalizeWords(memorial.firstName)} {memorial.middleInitial ? memorial.middleInitial.charAt(0).toUpperCase() + '. ' : ''}{capitalizeWords(memorial.lastName)}
  </h2>

  {isLiving ? (
    <div className="mt-2 flex items-center justify-center gap-3 text-gray-500 text-sm md:text-base font-light" style={{ letterSpacing: '0.16em' }}>
      {memorial.birthDate && <span>Born {formatDate(memorial.birthDate)}</span>}
      {memorial.birthDate && <span className="w-6 h-px bg-gray-300" />}
      <span style={{ color: '#7d96c2' }}>Still shining among us</span>
    </div>
  ) : (memorial.birthDate || memorial.deathDate) ? (
    <div className="mt-2 flex items-center justify-center gap-3 text-gray-500 text-sm md:text-base font-light" style={{ letterSpacing: '0.16em' }}>
      <span>{formatDate(memorial.birthDate)}</span>
      <span className="w-6 h-px bg-gray-300" />
      <span>{formatDate(memorial.deathDate)}</span>
    </div>
  ) : canEditFamilyTree ? (
    /* Owner and family only — visitors never see the gap pointed out.
       The ask is framed as what it buys them: the days being held. */
    <Link
      to={memorial.slug ? `/memorial/${memorial.slug}/edit` : `/edit/${memorialId}`}
      className="mt-3 inline-block text-xs px-5 py-2.5 rounded-full transition-colors"
      style={{ letterSpacing: '0.12em', color: '#96793c', border: '1px dashed #d8c9a4', background: '#faf6ec', textDecoration: 'none' }}
    >
      ✦ Add their dates &mdash; and we&rsquo;ll hold their birthday and remembrance day for you
    </Link>
  ) : null}

  <div className="mt-3 flex justify-center">
    <SubscriberBadge
      isActive={creatorSubscription && isSubscriptionActive(creatorSubscription)}
      isLoading={checkingSubscription}
    />
  </div>

  {memorial.galaxyLocation?.starId && (
    <div className="mt-4">
      <Link
        to={`/galaxy?star=${memorial.galaxyLocation.starId}`}
        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-[11px] uppercase transition-shadow hover:shadow-[0_0_24px_rgba(255,215,140,0.35)]"
        style={{
          letterSpacing: '0.22em',
          color: isLiving ? '#c9dbf7' : '#ffe2a8',
          border: isLiving ? '1px solid rgba(184,205,240,0.45)' : '1px solid rgba(184,150,79,0.45)',
          background: 'linear-gradient(180deg, #171426, #0c0a18)',
        }}
      >
        <span>✦ {memorial.galaxyLocation.starName || 'Their Star'}</span>
        <span className="text-white/35">·</span>
        <span>{memorial.galaxyLocation.region}</span>
        <span className="text-white/35">·</span>
        <span>Visit in the Galaxy</span>
      </Link>
    </div>
  )}
</div>
      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-3 md:px-6 mt-8 relative z-10">
        {/* Forever (pre-need) — still shining among us */}
        {isLiving && (
          <div className="mt-8">
            <LivingStarBanner
              memorial={memorial}
              memorialId={memorialId}
              canActivate={isMemorialOwner() || isSteward}
            />
          </div>
        )}

        {/* Days of remembrance — countdown, or the day itself */}
        {!isLiving && (
          <div className="mt-8">
            <RemembranceDates memorial={memorial} />
          </div>
        )}

        {/* Light their star — a visible act of tribute in the galaxy */}
        {!isLiving && memorial.galaxyLocation?.starId && (
          <LightStar memorial={memorial} memorialId={memorialId} />
        )}

        {/* Tribute row — quiet invitations rather than tallies */}
        <div className="mb-10">
          <div className="flex items-center justify-center gap-2 md:gap-3 flex-wrap">
            {!isLiving && (<>
            <button
              onClick={scrollToFlowersSection}
              className="flex items-center gap-2 px-4 md:px-5 py-2.5 bg-white rounded-full text-sm text-gray-600 border border-[#eae6dd] hover:border-[#d8c9a4] hover:shadow-[0_4px_18px_rgba(184,150,79,0.12)] transition-all"
            >
              <span>🌹</span>
              <span>
                {flowers.length === 0
                  ? <><em className="not-italic font-medium" style={{ color: '#b8964f' }}>Be the first</em> to leave a flower</>
                  : <>{flowers.length} Flower Card{flowers.length !== 1 ? 's' : ''}</>}
              </span>
            </button>

            <button
              onClick={scrollToCardsSection}
              className="flex items-center gap-2 px-4 md:px-5 py-2.5 bg-white rounded-full text-sm text-gray-600 border border-[#eae6dd] hover:border-[#d8c9a4] hover:shadow-[0_4px_18px_rgba(184,150,79,0.12)] transition-all"
            >
              <span>💌</span>
              <span>
                {cards.length === 0
                  ? <>Send a card</>
                  : <>{cards.length} Digital Card{cards.length !== 1 ? 's' : ''}</>}
              </span>
            </button>

            <button
              onClick={scrollToMemoryWall}
              className="flex items-center gap-2 px-4 md:px-5 py-2.5 bg-white rounded-full text-sm text-gray-600 border border-[#eae6dd] hover:border-[#d8c9a4] hover:shadow-[0_4px_18px_rgba(184,150,79,0.12)] transition-all"
            >
              <span>🕯️</span>
              <span>
                {Object.values(treasureCounts).reduce((sum, count) => sum + count, 0) === 0
                  ? <>Share a memory</>
                  : <>{Object.values(treasureCounts).reduce((sum, count) => sum + count, 0)} Memory Treasures</>}
              </span>
            </button>

            {isDonationsEnabled() && (
              <button
                onClick={() => setShowDonateQR(true)}
                data-action="donate"
                className="flex items-center gap-2 px-4 md:px-5 py-2.5 rounded-full text-sm text-white bg-[#274a35] hover:bg-[#1e3829] transition-all"
              >
                <span>🤲</span>
                <span>Donate to Family</span>
              </button>
            )}
            </>)}

            {(!auth.currentUser || memorial.createdBy !== auth.currentUser.uid) && (
              <button
                onClick={handleFollowToggle}
                disabled={followLoading}
                className={`flex items-center gap-2 px-4 md:px-5 py-2.5 rounded-full text-sm transition-all ${
                  isFollowing
                    ? 'bg-rose-600 text-white hover:bg-rose-700 border border-rose-600'
                    : 'bg-white text-gray-600 border border-[#eae6dd] hover:border-[#d8c9a4] hover:shadow-[0_4px_18px_rgba(184,150,79,0.12)]'
                } ${followLoading ? 'opacity-50 cursor-wait' : ''}`}
              >
                <span>{isFollowing ? '♥' : '♡'}</span>
                <span>{isFollowing ? 'Following' : 'Follow this memorial'}</span>
                {followerCount > 0 && (
                  <span className="text-xs opacity-75">({followerCount})</span>
                )}
              </button>
            )}

            {auth.currentUser && memorial.createdBy === auth.currentUser.uid && (
              <button
                onClick={() => navigate(`/memorial/${slug}/edit`)}
                className="flex items-center gap-2 px-4 md:px-5 py-2.5 rounded-full text-sm bg-black text-white hover:bg-gray-900 transition-all"
              >
                <span>✏️</span>
                <span>Edit</span>
              </button>
            )}
          </div>
        </div>

        {/* Finish-later prompts — owner only, one gentle piece at a time */}
        {auth.currentUser && memorial.createdBy === auth.currentUser.uid &&
          (!memorial.bio || !(memorial.images?.length > 0) || !memorial.galaxyLocation?.starId) && (
          <div className="mb-8 rounded-2xl border border-dashed border-[#d8c9a4] bg-[#fdfbf6] p-6 text-center">
            <p className="text-[10.5px] uppercase mb-2" style={{ letterSpacing: '0.3em', color: '#b8964f' }}>
              Their Memorial Is Growing
            </p>
            <p className="text-sm text-gray-500 font-light mb-4">
              A memorial isn't built in a day — add a little whenever you're ready.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {!memorial.bio && (
                <button
                  onClick={() => navigate(`/memorial/${slug}/edit`)}
                  className="px-5 py-2.5 rounded-full text-xs text-gray-600 bg-white border border-[#eae6dd] hover:border-[#d8c9a4] hover:shadow-[0_4px_18px_rgba(184,150,79,0.12)] transition-all"
                >
                  ✍️ Add their story
                </button>
              )}
              {!(memorial.images?.length > 0) && (
                <button
                  onClick={() => navigate(`/memorial/${slug}/edit`)}
                  className="px-5 py-2.5 rounded-full text-xs text-gray-600 bg-white border border-[#eae6dd] hover:border-[#d8c9a4] hover:shadow-[0_4px_18px_rgba(184,150,79,0.12)] transition-all"
                >
                  📷 Add their photo
                </button>
              )}
              {!memorial.galaxyLocation?.starId && (
                <Link
                  to="/"
                  className="px-5 py-2.5 rounded-full text-xs bg-white border border-[#eae6dd] hover:border-[#d8c9a4] hover:shadow-[0_4px_18px_rgba(184,150,79,0.12)] transition-all"
                  style={{ color: '#96793c', textDecoration: 'none' }}
                >
                  ✦ Choose their star
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Candlelight vigil — the one-second gesture of care */}
        {!isLiving && <CandleVigil memorialId={memorialId} memorial={memorial} />}

        {/* Bio Section — refined, celestial accents */}
        {memorial.bio && (
          <div className="bg-white p-6 md:p-10 mb-8 border border-[#eee9df]">
            <div className="text-center mb-9">
              <p className="text-[10.5px] uppercase" style={{ letterSpacing: '0.34em', color: '#b8964f' }}>
                {isLiving ? 'In Their Own Words' : 'In Loving Memory'}
              </p>
              <h2 className="mt-3 text-2xl md:text-3xl font-extralight text-gray-900" style={{ letterSpacing: '0.04em' }}>
                {memorial.memorialTitle || (isLiving ? 'A Story Still Being Written' : 'A Life Well Lived')}
              </h2>
              <div
                className="mx-auto mt-4"
                style={{ width: '54px', height: '1px', background: 'linear-gradient(90deg, transparent, #cbab6a, transparent)' }}
              />
            </div>
            <div className="max-w-none">
              <p className="whitespace-pre-line text-base md:text-lg font-light text-gray-600" style={{ lineHeight: '1.95' }}>
                {showFullBio ? memorial.bio : (memorial.bio.length > 800 ? memorial.bio.slice(0, 800) + '…' : memorial.bio)}
              </p>

              {memorial.bio.length > 800 && (
                <button
                  onClick={() => setShowFullBio(!showFullBio)}
                  className="mt-6 text-xs uppercase text-gray-700 hover:text-black underline underline-offset-4 transition-colors"
                  style={{ letterSpacing: '0.22em' }}
                >
                  {showFullBio ? 'Show Less' : 'Read More'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Gallery Section — Facebook-style collage + viewer */}
        {memorial.images?.length > 0 && (
          <SimpleGallery
            images={memorial.images}
            memorialName={`${memorial.firstName} ${memorial.lastName}`}
            canShowAll={!checkingSubscription && !!(creatorSubscription && isSubscriptionActive(creatorSubscription))}
            isOwner={!!auth.currentUser && memorial.createdBy === auth.currentUser.uid}
            checking={checkingSubscription}
          />
        )}

        {/* Service Invitation & RSVP — where, when, and who is coming */}
        <ServiceInvitation
          memorial={memorial}
          memorialId={memorialId}
          isFamily={canEditFamilyTree}
        />

        {/* Service Program Section - Optional */}
        <ServiceProgramSection memorial={memorial} />

        {/* Life Timeline Section */}
        <MemorialTimeline memorial={memorial} />

        {/* Family Tree — the branches they belong to. Owner and invited
            family can build it together. */}
        {/* Display only. Building the tree happens in the edit form, with the
            rest of the memorial's details — this just points there. */}
        <FamilyTreeSection
          memorial={memorial}
          memorialId={memorialId}
          canEdit={canEditFamilyTree}
          editHref={memorial?.slug ? `/memorial/${memorial.slug}/edit` : `/edit/${memorialId}`}
        />

{/* -------------------- Memory Wall Section -------------------- */}
        <div id="memory-wall-section" className="bg-white p-6 md:p-10 mb-8 border border-[#eee9df]">
          <div className="text-center mb-8">
            <p className="text-[10.5px] uppercase" style={{ letterSpacing: '0.34em', color: '#b8964f' }}>
              Memory Wall
            </p>
            <h2 className="mt-3 text-2xl md:text-3xl font-extralight text-gray-900" style={{ letterSpacing: '0.04em' }}>
              As We Remember Them
            </h2>
            <div
              className="mx-auto mt-4"
              style={{ width: '54px', height: '1px', background: 'linear-gradient(90deg, transparent, #cbab6a, transparent)' }}
            />
            <p className="mt-4 text-gray-500 text-sm font-light">
              Share your favorite memories and treasure the ones that touch your heart
            </p>
          </div>

          {auth.currentUser ? (
            <div className="max-w-3xl mx-auto mb-8">
              <div className="bg-white rounded-2xl p-6 border border-[#eee9df]">
                {/* Memory prompts — a question invites where a blank box intimidates */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {MEMORY_PROMPTS.slice(0, 3).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setMemoryPrompt(memoryPrompt === p ? '' : p)}
                      className={`text-[11px] px-3 py-1.5 rounded-full border transition-all ${
                        memoryPrompt === p
                          ? 'border-[#cbab6a] bg-[#faf6ec] text-[#96793c]'
                          : 'border-[#eae6dd] bg-white text-gray-500 hover:border-[#d8c9a4]'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
                <textarea
                  rows={4}
                  placeholder={memoryPrompt || "Share a cherished memory, a funny story, or how they touched your life..."}
                  value={memoryText}
                  onChange={e => setMemoryText(e.target.value.slice(0, 500))}
                  maxLength={500}
                  className="w-full px-4 py-3 bg-[#faf9f7] border border-[#eae6dd] rounded-xl focus:outline-none focus:border-[#cbab6a] focus:shadow-[0_0_18px_rgba(255,215,140,0.15)] resize-none text-gray-700 placeholder-gray-400 transition duration-300"
                />
                <div className="mt-4">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-xs text-gray-500">
                      {memoryText.length}/500 characters
                    </span>
                  </div>
                  <div className="flex justify-center">
                    <button
                      onClick={handleSubmitMemory}
                      disabled={!memoryText.trim() || submittingMemory}
                      className="sm-btn-primary"
                    >
                      <span className="text-lg">🕊️</span>
                      {submittingMemory ? 'Sharing...' : 'Share Memory'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center mb-8 bg-white/50 backdrop-blur-sm p-6">
              <p className="text-gray-600 mb-4">Please log in to share a memory</p>
              <button
                onClick={handleLoginRedirect}
                className="sm-btn-primary"
              >
                Log In to Share
              </button>
            </div>
          )}

          <div className="space-y-6">
            {sortedMemories.length > 0 ? (
              <>
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-light text-gray-800">
                    Shared Memories ({sortedMemories.length})
                  </h3>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span>Most treasured first</span>
                    <div className="w-2 h-2 rounded-full" style={{ background: '#cbab6a' }}></div>
                  </div>
                </div>
                
                <div className="grid gap-6">
                  {sortedMemories.slice(0, displayedMemoryCount).map((memory, idx) => (
                    <MemoryCard 
                      key={memory.id} 
                      memory={memory} 
                      index={idx}
                      expandedMemories={expandedMemories}
                      setExpandedMemories={setExpandedMemories}
                      showComments={showComments}
                      setShowComments={setShowComments}
                      memoryComments={memoryComments}
                      commentTexts={commentTexts}
                      setCommentTexts={setCommentTexts}
                      submittingComments={submittingComments}
                      handleSubmitComment={handleSubmitComment}
                      getRelativeTime={getRelativeTime}
                      getUserDisplayName={getUserDisplayName}
                      auth={auth}
                      handleLoginRedirect={handleLoginRedirect}
                      treasureCounts={treasureCounts}
                      userTreasures={userTreasures}
                      handleTreasureMemory={handleTreasureMemory}
                      treasuringMemories={treasuringMemories}
                      handleDeleteComment={handleDeleteComment}
                      memorialId={memorialId}
                      isMemorialOwner={isMemorialOwner}
                      editingComments={editingComments}
                      editCommentTexts={editCommentTexts}
                      setEditingComments={setEditingComments}
                      setEditCommentTexts={setEditCommentTexts}
                      handleEditComment={handleEditComment}
                      handleSaveComment={handleSaveComment}
                      handleCancelEdit={handleCancelEdit}
                    />
                  ))}
                </div>

                {sortedMemories.length > displayedMemoryCount && (
                  <div className="text-center pt-6">
                    <button 
                      onClick={() => setDisplayedMemoryCount(prev => prev + 2)}
                      className="sm-btn-primary"
                    >
                      Load {Math.min(2, sortedMemories.length - displayedMemoryCount)} more memories
                    </button>
                  </div>
                )}

                {displayedMemoryCount > 2 && (
                  <div className="text-center pt-3">
                    <button 
                      onClick={() => setDisplayedMemoryCount(2)}
                      className="text-black hover:text-gray-900 font-medium text-sm hover:bg-gray-100 px-6 py-3 transition duration-300"
                    >
                      Show less memories
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🕊️</div>
                <p className="text-gray-500 text-lg font-light">
                  {auth.currentUser 
                    ? "Be the first to share a memory" 
                    : "No memories shared yet"}
                </p>
              </div>
            )}
          </div>
        </div>
{/* -------------------- End Memory Wall Section -------------------- */}

{/* -------------------- Digital Flowers Section -------------------- */}
        {!isLiving && (
        <DigitalFlowersSection
          memorial={memorial}
          memorialId={memorialId}
          slug={slug}
          auth={auth}
          db={db}
          handleLoginRedirect={handleLoginRedirect}
        />
        )}
{/* -------------------- End Digital Flowers Section -------------------- */}

{/* -------------------- Digital Cards Section -------------------- */}
        {!isLiving && (
        <DigitalCardsSection
          memorial={memorial}
          memorialId={memorialId}
          slug={slug}
          auth={auth}
          db={db}
          handleLoginRedirect={handleLoginRedirect}
        />
        )}
{/* -------------------- End Digital Cards Section -------------------- */}

{/* -------------------- Fundraising Section -------------------- */}
        {!isLiving && (
        <FundraisingSection
          memorial={memorial}
          memorialId={memorialId}
          slug={slug}
          auth={auth}
          db={db}
          creatorSubscription={creatorSubscription}
          checkingSubscription={checkingSubscription}
          isSubscriptionActive={isSubscriptionActive}
          showDonateQR={showDonateQR}
          setShowDonateQR={setShowDonateQR}
          activeTab={donationActiveTab}
          setActiveTab={setDonationActiveTab}
        />
        )}
{/* -------------------- End Fundraising Section -------------------- */}

        {/* Letters to Heaven Section */}
        {!isLiving && (
        <LettersToHeaven
          memorial={memorial}
          memorialId={memorialId}
          auth={auth}
          db={db}
          getUserDisplayName={getUserDisplayName}
          handleLoginRedirect={handleLoginRedirect}
          capitalizeWords={capitalizeWords}
        />
        )}

        {/* Favorites Section */}
        {memorial.favorites && memorial.favorites.length > 0 && (
          <FavoritesSection favorites={memorial.favorites} memorial={memorial} />
        )}

        {/* Memorial Tribute Video Section */}
        {youtubeVideoId && (
          <div className="bg-white p-6 md:p-10 mb-8 border border-[#eee9df]">
            <div className="text-center mb-8">
              <p className="text-[10.5px] uppercase" style={{ letterSpacing: '0.34em', color: '#b8964f' }}>
                In Motion & Memory
              </p>
              <h2 className="mt-3 text-2xl md:text-3xl font-extralight text-gray-900" style={{ letterSpacing: '0.04em' }}>
                Memorial Tribute Video
              </h2>
              <div
                className="mx-auto mt-4"
                style={{ width: '54px', height: '1px', background: 'linear-gradient(90deg, transparent, #cbab6a, transparent)' }}
              />
            </div>
            
            <div className="max-w-4xl mx-auto">
              {!isVideoPlaying ? (
                // Custom Thumbnail with Memorial Picture and Heavenly Background
                <div 
                  className="relative cursor-pointer group overflow-hidden aspect-video rounded-2xl border border-[#cbab6a] shadow-xl"
                  onClick={handleVideoPlay}
                >
                  {/* Night sky */}
                  <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 20%, #1a1530 0%, #05040d 80%)' }}>
                    <div className="absolute top-[18%] left-[12%] w-1 h-1 rounded-full bg-[#ffe2a8] opacity-60 animate-pulse"></div>
                    <div className="absolute top-[30%] right-[18%] w-1 h-1 rounded-full bg-[#c9d4ef] opacity-50 animate-pulse delay-300"></div>
                    <div className="absolute bottom-[24%] left-[28%] w-0.5 h-0.5 rounded-full bg-[#c9d4ef] opacity-60"></div>
                    <div className="absolute top-[12%] right-[38%] w-0.5 h-0.5 rounded-full bg-[#ffe2a8] opacity-40"></div>
                    <div className="absolute bottom-[16%] right-[26%] w-1 h-1 rounded-full bg-[#ffe2a8] opacity-50 animate-pulse delay-700"></div>
                    <div className="absolute top-[45%] left-[40%] w-0.5 h-0.5 rounded-full bg-[#c9d4ef] opacity-50"></div>
                  </div>
                  
                  {/* Memorial Image Overlay */}
                  {memorial.images?.[0] && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="relative">
                        <img
                          src={memorial.images[0]}
                          alt={`${memorial.firstName} ${memorial.lastName}`}
                          className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover transition-transform duration-500 group-hover:scale-105 shadow-[0_0_0_2px_rgba(255,255,255,0.9),0_0_0_3px_rgba(203,171,106,0.7),0_0_40px_rgba(255,210,130,0.45)]"
                          onError={e => (e.target.style.display = 'none')}
                        />
                        {/* Heavenly glow around image */}
                        <div className="absolute -inset-3 rounded-full border border-[#e5c883]/50 animate-pulse"></div>
                        <div className="absolute -inset-6 rounded-full border border-[#e5c883]/20 animate-pulse delay-300"></div>
                      </div>
                    </div>
                  )}
                  
                  {/* Play Button */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-black/40 backdrop-blur-sm border border-[#e5c883]/70 rounded-full p-6 shadow-xl transition-all duration-300 group-hover:bg-black/60 group-hover:scale-110">
                      <div className="w-0 h-0 border-l-[20px] border-l-[#ffe9b8] border-t-[14px] border-t-transparent border-b-[14px] border-b-transparent ml-1 drop-shadow-lg"></div>
                    </div>
                  </div>
                  
                  {/* Video Title Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 via-black/40 to-transparent p-6">
                    <h3 className="text-white text-lg md:text-xl font-light text-center drop-shadow-lg">
                      Memorial Tribute for {capitalizeWords(memorial.firstName)} {capitalizeWords(memorial.lastName)}
                    </h3>
                    <p className="text-white/80 text-sm text-center mt-1 drop-shadow">
                      Click to play tribute video
                    </p>
                  </div>
                  
                  {/* Hover Effects */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#e5c883]/0 via-transparent to-[#e5c883]/0 group-hover:from-[#e5c883]/10 group-hover:to-[#e5c883]/5 transition-all duration-500"></div>
                </div>
              ) : (
                // YouTube Embed Player
                <div className="relative overflow-hidden aspect-video rounded-2xl border border-[#cbab6a] shadow-xl">
                  <iframe
                    src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&rel=0&modestbranding=1`}
                    title="Memorial Tribute Video"
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              )}
              
              {/* Video Description */}
              <div className="text-center mt-6">
                <p className="text-gray-500 text-sm font-light">
                  A special tribute video celebrating the life and memory of {capitalizeWords(memorial.firstName)} {capitalizeWords(memorial.lastName)}
                </p>
              </div>
            </div>
          </div>
        )}

{/* Final Resting Place Section - Peaceful Closing */}
{!isLiving && <FinalRestingPlace memorial={memorial} />}

{/* Their constellation — the last section, right above the maintenance card */}
<NeighboringStars memorial={memorial} />

{/* Floating quick-switcher back to the galaxy */}
<Link
  to={memorial.galaxyLocation?.starId ? `/galaxy?star=${memorial.galaxyLocation.starId}` : '/'}
  className={`fixed bottom-6 left-6 z-40 flex items-center gap-2 px-4 py-2.5 rounded-full text-[11px] uppercase transition-all duration-500 hover:shadow-[0_0_24px_rgba(255,215,140,0.35)] ${
    showFloating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
  }`}
  style={{
    letterSpacing: '0.18em',
    color: '#ffe2a8',
    border: '1px solid rgba(184,150,79,0.5)',
    background: 'linear-gradient(180deg, #171426, #0c0a18)',
    textDecoration: 'none',
  }}
  title={memorial.galaxyLocation?.starId ? 'See their star in the galaxy' : 'Visit the Galaxy of Memorials'}
>
  <span aria-hidden="true">🌌</span>
  <span>{memorial.galaxyLocation?.starId ? 'Their Star' : 'The Galaxy'}</span>
</Link>

      </div>

      {/* Profile maintenance info — quiet closing card */}
      <div className="max-w-4xl mx-auto px-2 md:px-6 mb-8">
        <div className="bg-white p-5 md:p-7 border border-[#eee9df]">
          <div className="text-center">
            <p className="text-[10.5px] uppercase mb-2" style={{ letterSpacing: '0.34em', color: '#b8964f' }}>
              In Trusted Hands
            </p>
            <p className="text-sm md:text-base font-light text-gray-800 mb-1 md:mb-2" style={{ letterSpacing: '0.04em' }}>
              <span className="block md:hidden">Maintained by {getCreatorName()}</span>
              <span className="hidden md:block">Memorial Tribute Maintained by {getCreatorName()}</span>
            </p>
            <p className="text-xs md:text-sm text-gray-500">
              <span className="block md:hidden">Updated: {memorial.updatedAt?.toDate ? 
                memorial.updatedAt.toDate().toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                }) :
                memorial.createdAt?.toDate ?
                  memorial.createdAt.toDate().toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  }) :
                  new Date().toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })
              }</span>
              <span className="hidden md:block">Last update: {memorial.updatedAt?.toDate ? 
                memorial.updatedAt.toDate().toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) :
                memorial.createdAt?.toDate ?
                  memorial.createdAt.toDate().toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) :
                  new Date().toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })
              }</span>
            </p>
          </div>

          {/* Memorial Security Info */}
          <div
            className="mt-4 pt-4"
            style={{ borderTop: '1px solid transparent', borderImage: 'linear-gradient(90deg, transparent, #cbab6a, transparent) 1' }}
          >
            <div className="flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center gap-1.5 text-sm">
                <span style={{ color: '#b8964f' }}>🔒</span>
                <span className="hidden sm:inline">This memorial is publicly accessible but securely managed</span>
                <span className="sm:hidden">Publicly accessible & secure</span>
              </div>
              <span>Memorial ID: {memorialId?.slice(-8)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white/80 backdrop-blur-sm border-t border-gray-100 py-8 mt-16" style={{ fontFamily: 'Garamond, serif' }}>
        <div className="max-w-4xl mx-auto px-2 md:px-6 text-center">
          <p className="text-sm text-gray-500 font-light">
            © {new Date().getFullYear()} SouleMap Memorials • Celebrating lives, preserving memories
          </p>
        </div>
      </footer>
    </div>
  );
}
